/* File: frontend/src/pages/AIParalegal.js */
/* Version: v4.0 (Fitenko Law AI Immigration Operating System — светлый общий слой) */
/* Description:
   - 3-Column Modern Workspace:
     * Col 1: Cases Docket & Work Progress Bar directly underneath.
     * Col 2: USCIS Practice Selector, Context, Dynamic Checklist & Document Dropzone.
     * Col 3: Multi-Agent Consilium Arena (Ekaterina Fitenko, Alexander Vance, Elena, Marcus, Katifi)
   - Interactive Lead Attorney Directive Box (Ekaterina Fitenko, Esq. - Human in the Loop).
   - Katifi AI Daily Docket Audit & Obsidian Vault Scribe.

   🔴 ЦВЕТ ЗДЕСЬ БОЛЬШЕ НЕ ЗАШИТ НИ РАЗУ (quick-260912-23z, 12.09.2026).
      Было 466 мест, стало 0: каждая утилита, несшая значение цвета в скобках,
      переведена на токен по ЗАКРЫТОМУ словарю из 45 пар (префикс, значение),
      где паре отвечает РОЛЬ, а роли — токен. Словарь записан в шапке `AIParalegal.css` — он единственный
      держатель соответствия, и по нему судят. Роль решает СВОЙСТВО, а не hex:
      тёмная поверхность фоном карточки — это `--surface`, а тот же по глубине
      цвет дорожкой шкалы — уже `--line`. 🔴 Ни одного значения цвета в этом
      файле не приведено даже ПРОЗОЙ: прибор считает вхождения В ФАЙЛЕ, и проза
      для него неотличима от кода.
   🔴 ГАРНИТУРА ЭТИМ ЭКРАНОМ НЕ НАЗНАЧАЕТСЯ. Заголовочная и интерфейсная
      приходят каскадом от `html.fitenko` (`styles/fitenko-tokens.css`). Прежнее
      назначение Cormorant Garamond осталось ТОЛЬКО на запасном тёмном пути для
      не-fitenko арендаторов — см. шапку `AIParalegal.css`.
   🔴 ТЁМНАЯ ПОЛОСА НА ЭКРАНЕ РОВНО ОДНА — ряд пяти агентов консилиума
      (`.ap-konsilium`). Решение владельца Д-02. Значение приходит токеном
      `--council-bg` и больше ниоткуда.
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Scale, Briefcase, Plus, Search, TrendingUp, ClipboardCheck,
  UploadCloud, Play, MessagesSquare, FileText, CheckCircle,
  AlertTriangle, ListOrdered, Check, Sparkles, X, User,
  HeartHandshake, PlaneTakeoff, Loader2, RotateCcw
} from 'lucide-react';
import apiService from '../services/apiService';
import { ShapkaSKnopkoy, Sostoyanie } from '../components/FitenkoUzor';
import { takeQuestion, CONSILIUM_QUESTION_EVENT } from '../services/consiliumBridge';
import { pollConsiliumJob } from '../services/consiliumJob';
import './AIParalegal.css';

/* Источник текста документа — честная подпись под карточкой.
   needs_ocr === true означает «прочитать не удалось»: это обязано быть видно адвокату,
   а не тонуть в консоли. Пустой скан раньше выглядел как обычный проверенный документ. */
const documentSourceLabel = (doc) => {
  if (!doc) return { text: "источник текста неизвестен", tone: "text-[var(--ink-muted-ink)]" };
  if (doc.needs_ocr === true) {
    return { text: "НЕ РАСПОЗНАНО — нужна ручная проверка", tone: "text-[var(--red-accent-ink)]" };
  }
  switch (doc.text_source) {
    case 'pdf_text_layer':
      return { text: "текстовый слой", tone: "text-[var(--green-accent-ink)]" };
    case 'ocr_gemini':
      return { text: `распознано (${doc.ocr_model || "модель не названа"})`, tone: "text-[var(--amber-accent-ink)]" };
    case 'plain':
      return { text: "обычный текст", tone: "text-[var(--green-accent-ink)]" };
    default:
      return { text: "источник текста не указан", tone: "text-[var(--ink-muted-ink)]" };
  }
};

/* Число страниц показываем ТОЛЬКО там, где оно известно.
   page_count_known === false → «—». У DOCX/TXT страниц нет вовсе, а прежнее
   max(1, len(text)/2500) было выдумкой, которую фронт подписывал как «страниц». */
const pagesLabel = (doc) => (
  doc && doc.page_count_known === true && doc.pages_total !== null && doc.pages_total !== undefined
    ? String(doc.pages_total)
    : "—"
);

/* Идентификаторы трёх ДЕМО-записей, которые до 05.09.2026 лежали в коде как
   «дела фирмы»: выдуманные клиенты, выдуманные фабулы, выдуманные проценты
   готовности. Массив с ними удалён целиком. Строки остались ровно для одного —
   отличить работу человека от заготовки при переносе браузерного докета.
   Отбор всё равно делает сервер: рубеж должен быть один. */
const DEMO_CASE_IDS = ["case-01", "case-02", "case-03"];

/* Ключи браузерного хранилища. Исходный НЕ перезаписывается и НЕ удаляется:
   если перенос окажется неверным, единственная копия данных обязана уцелеть. */
const LOCAL_DOCKET_KEY = 'fitenko_cases_db_v1';
const MIGRATED_MARKER_KEY = 'fitenko_docket_migrated_v1';
const ARCHIVE_KEY_PREFIX = 'fitenko_cases_db_v1__archive';

/* 🔴 Подписи измеряемых величин. Три состояния, а не два:
     null/undefined — не считали → прочерк;
     0              — посчитали, вышел ноль → «0%».
   Слить их в одно (value || 0) значит показать адвокату «0 % готовности» там,
   где сверки вообще не было. */
const pctLabel = (value) => (value === null || value === undefined ? '—' : String(value) + '%');

/* Ширина полосы прогресса. Для «не считали» полоса нулевая, но подпись — прочерк:
   пустая полоса без числа ничего не утверждает. */
const pctWidth = (value) => (value === null || value === undefined ? 0 : Number(value));

/* Готовность к подаче: ТРИ разные подписи. «Не проверяли» — не то же самое,
   что «не готово». */
const readinessLabel = (value) => {
  if (value === null || value === undefined) return 'Готовность не проверялась';
  return value ? 'Готово к подаче' : 'Не готово к подаче';
};

/* Русские названия практик. Список ДОПУСТИМЫХ ключей берётся с сервера
   (/status -> supported_practice_areas); здесь только подписи к ним. Ключа нет в
   этой таблице — показываем сам ключ, а не подставляем чужое название. */
const PRACTICE_TITLES_RU = {
  marriage_green_card: 'Грин-карта по браку (I-130 / I-485)',
  family_petition: 'Семейная петиция (родители, дети, братья/сёстры)',
  employment_eb1: 'Рабочая иммиграция EB-1 (выдающиеся способности, профессора и исследователи)',
  employment_eb2_niw: 'Рабочая иммиграция EB-2 NIW (национальный интерес)',
  employment_eb2_perm: 'Рабочая иммиграция EB-2 через трудовую сертификацию (PERM)',
  employment_eb3: 'Рабочая иммиграция EB-3 (квалифицированные работники и специалисты)',
  employment_eb5_investor: 'EB-5: инвесторская грин-карта (I-526 / I-829)',
  employment_e2_treaty_investor: 'E-2: инвестор из договорной страны (неиммиграционная)',
  asylum_i589: 'Политическое убежище (I-589)',
  humanitarian_vawa: 'Самопетиция VAWA (I-360)',
  humanitarian_tps: 'Временная защита TPS (I-821)',
  naturalization_n400: 'Натурализация и гражданство (N-400)',
  consular_processing_nvc: 'Консульская обработка через NVC (DS-260)'
};

/* Строка докета с сервера -> объект экрана. null НЕ приводится к 0/false/''. */
const normalizeDocketRow = (row) => ({
  id: row.case_id,
  caseNumber: row.case_number,
  title: row.title,
  status: row.status,
  createdAt: row.created_at,
  practiceArea: row.practice_area,
  caseContext: row.case_context,
  completionPercentage: row.completion_percentage,
  attachedPercentage: row.attached_percentage,
  isReadyForFiling: row.is_ready_for_filing,
  riskScore: row.risk_score,
  riskLevel: row.risk_level,
  lastAction: row.last_action,
  lastConsiliumAt: row.last_consilium_at,
  lastConsiliumStatus: row.last_consilium_status,
  importedFromLocalId: row.imported_from_local_id
});

/* Читает браузерный докет как МАТЕРИАЛ ДЛЯ ПЕРЕНОСА. Ничего не пишет. */
const readLocalDocket = () => {
  try {
    const saved = localStorage.getItem(LOCAL_DOCKET_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("Локальный докет не прочитан", e);
    return [];
  }
};

const AIParalegal = () => {
  /* 🔴 Источник докета — сервер, не браузер. Пустой массив сам по себе ничего не
     значит: «дел нет» показываем только когда докет ЗАГРУЖЕН (docketLoaded). */
  const [cases, setCases] = useState([]);
  const [docketLoaded, setDocketLoaded] = useState(false);
  const [docketError, setDocketError] = useState(null);
  const [isDocketLoading, setIsDocketLoading] = useState(false);
  const [visibilityScope, setVisibilityScope] = useState(null);

  /* Записи, найденные в браузере и ещё не перенесённые. */
  const [localDocketItems, setLocalDocketItems] = useState([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationError, setMigrationError] = useState(null);

  const [activeCaseId, setActiveCaseId] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  /* 🔴 Пусто, а не практика по умолчанию. Молчаливо подставленная практика
     уводит консилиум на ЧУЖИЕ прецеденты: 07.09.2026 вопрос про E-2 разбирался
     под грин-картой по браку, и в разделе «Прецеденты» стояли дела по браку. */
  const [selectedPractice, setSelectedPractice] = useState('');
  /* Что определила система и чей это выбор. 🔴 Два РАЗНЫХ признака: определение
     предзаполняет селектор, но НИКОГДА не затирает выбранное человеком. */
  const [inferredPractice, setInferredPractice] = useState(null);
  const [practiceChosenByHuman, setPracticeChosenByHuman] = useState(false);
  const [caseContext, setCaseContext] = useState('');
  const [checklistData, setChecklistData] = useState(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('transcript'); // 'transcript' | 'memo' | 'gap'
  const [isConsiliumRunning, setIsConsiliumRunning] = useState(false);
  const [consiliumResults, setConsiliumResults] = useState(null);
  /* Вопрос без дела: медленная полоса. Быстрый ответ живёт в виджете Катифи. */
  const [questionText, setQuestionText] = useState('');
  /* 🔴 Идентификатор последней завершённой работы. Без него справку нечем
     запросить: читается она ТОЛЬКО по идентификатору, путь снаружи сервер не
     принимает. До 07.09.2026 идентификатор нигде не сохранялся, и справка на
     17 944 байта оставалась недостижимой при штатно отработавшем прогоне. */
  const [lastJobId, setLastJobId] = useState(null);
  const [isBriefLoading, setIsBriefLoading] = useState(false);

  // Attorney Ekaterina Notes & Katifi Reporting State
  const [attorneyNotes, setAttorneyNotes] = useState('');
  const [attorneyDirectivesLog, setAttorneyDirectivesLog] = useState([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [katifiReportText, setKatifiReportText] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [obsidianPath, setObsidianPath] = useState('');
  // Имена моделей в шапке НЕ зашиваются литералами: 05.09 бейджи год показывали
  // «Claude 3.7» и «Gemini 2.0», когда обе модели уже отвечали 404. Берём из
  // /status, чтобы бейдж не мог разойтись с конфигурацией.
  const [agentProviders, setAgentProviders] = useState(null);
  /* Реестр практик — с сервера. Зашитый на фронте перечень уже разошёлся с
     бэкендом: в <select> был citizenship_n400, которого в реестре нет вовсе. */
  const [supportedPractices, setSupportedPractices] = useState([]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalClientName, setModalClientName] = useState('');
  /* Email и имя обязательны: без них бэкенд дело не заведёт — клиент в системе
     создаётся по email, а имя нужно для нового пользователя. */
  const [modalClientEmail, setModalClientEmail] = useState('');
  const [modalClientFirstName, setModalClientFirstName] = useState('');
  const [modalClientLastName, setModalClientLastName] = useState('');
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [modalCategory, setModalCategory] = useState('');
  const [modalCitizenship, setModalCitizenship] = useState('');
  const [modalANumber, setModalANumber] = useState('');
  const [modalContact, setModalContact] = useState('');
  const [modalSpouseName, setModalSpouseName] = useState('');
  const [modalSpouseStatus, setModalSpouseStatus] = useState('Гражданин США (по рождению)');
  const [modalMarriageDetails, setModalMarriageDetails] = useState('');
  const [modalDependents, setModalDependents] = useState('');
  const [modalEntryDate, setModalEntryDate] = useState('');
  const [modalPortOfEntry, setModalPortOfEntry] = useState('');
  const [modalClassOfAdmission, setModalClassOfAdmission] = useState('B2 Visitor');
  const [modalI94Date, setModalI94Date] = useState('');
  const [modalI94Ds, setModalI94Ds] = useState(false);
  const [modalFilingDeadline, setModalFilingDeadline] = useState('');
  const [modalCaseContext, setModalCaseContext] = useState('');
  const [modalFiles, setModalFiles] = useState([]);
  const [riskFlags, setRiskFlags] = useState({
    overstay: false,
    priorDenials: false,
    criminal: false,
    jointSponsor: false
  });

  const fileInputRef = useRef(null);

  /* Опрос прекращается при уходе со страницы: иначе размонтированный экран
     продолжал бы дёргать сервер десять минут и звать setState в никуда. */
  const pollCancelRef = useRef(false);
  useEffect(() => () => { pollCancelRef.current = true; }, []);

  /* Загрузка докета с сервера.
     🔴 При ошибке список НЕ обнуляется и «дел нет» не рисуется: отказ измерителя
     нельзя выдавать за ноль. Показывается плашка с причиной и кнопкой «Повторить». */
  const loadDocket = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setIsDocketLoading(true);
    try {
      const data = await apiService.makeRequest('/ai-paralegal/docket');
      const rows = (data?.cases || []).map(normalizeDocketRow);
      setCases(rows);
      setVisibilityScope(data?.visibility_scope || null);
      setDocketLoaded(true);
      setDocketError(null);
      setActiveCaseId(prev => (
        rows.some(r => r.id === prev) ? prev : (rows[0]?.id ?? null)
      ));
      return rows;
    } catch (e) {
      console.error("Докет не загружен", e);
      setDocketError(e.message || "причина не названа");
      return null;
    } finally {
      if (!silent) setIsDocketLoading(false);
    }
  }, []);

  useEffect(() => { loadDocket(); }, [loadDocket]);

  /* Вопрос, принесённый мостом из виджета, подставляется КАК ЕСТЬ — без trim.
     Подрезка здесь означала бы, что в консилиум уехал не тот текст, который
     человек написал, а заметить подмену было бы негде. */
  useEffect(() => {
    const pickUp = () => {
      const carried = takeQuestion();
      if (carried !== null) setQuestionText(carried);
    };
    pickUp();
    /* Второй путь подстановки — на случай, когда человек УЖЕ на этой странице:
       navigate() тогда ничего не перемонтирует, и одного эффекта монтирования
       не хватит. См. комментарий у CONSILIUM_QUESTION_EVENT. */
    window.addEventListener(CONSILIUM_QUESTION_EVENT, pickUp);
    return () => window.removeEventListener(CONSILIUM_QUESTION_EVENT, pickUp);
  }, []);

  const runQuestionConsilium = async () => {
    /* 🔴 Заставы по практике здесь НЕТ. Пустая практика — это «определи», а не
       отказ принять вопрос. Пустой вопрос отвергается по-прежнему: разбирать
       нечего физически. */
    if (!questionText.trim()) {
      toast.error("Вопрос пуст — разбирать нечего");
      return;
    }
    setIsConsiliumRunning(true);
    toast.loading(
      "Консилиум разбирает вопрос. Замер: 5 мин 24 с (324 с). Быстрый ответ живёт в виджете Катифи.",
      { id: "question-consilium" }
    );
    try {
      /* Постановка в очередь: ответ приходит секундами, соединение минутами не
         висит, и proxy_read_timeout хостинга перестаёт что-либо решать. */
      const res = await fetch('/api/v1/ai-paralegal/question/consilium', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* 🔴 `|| null`, а не пустая строка: null означает «ОПРЕДЕЛИ», пустая
           строка означала бы выбор пустоты и уехала бы в закрытый перечень. */
        body: JSON.stringify({ question: questionText, legal_area: selectedPractice || null })
      });
      if (!res.ok) {
        /* Код ответа называется. Молчаливый провал читался бы как «ничего не нашлось». */
        let detail = '';
        try { detail = (await res.json())?.detail || ''; } catch (e) { detail = ''; }
        throw new Error(`HTTP ${res.status}${detail ? `: ${detail}` : ''}`);
      }
      const accepted = await res.json();
      if (!accepted?.job_id) {
        throw new Error("сервер принял запрос, но идентификатора работы не выдал");
      }
      /* Определение показывается СРАЗУ, из 202-ответа: ждать пять минут, чтобы
         узнать, под какой практикой пошёл разбор, — поздно. */
      applyInference(accepted.practice_inference);

      const { outcome, payload } = await pollConsiliumJob({
        jobId: accepted.job_id,
        fetchImpl: (url) => fetch(url),
        isCancelled: () => pollCancelRef.current,
      });

      /* Пять исходов — пять РАЗНЫХ сообщений. Ни одно не притворяется другим. */
      if (outcome === 'done') {
        setConsiliumResults(payload.result);
        applyInference(payload.result?.practice_inference);
        setLastJobId(accepted.job_id);
        setActiveTab('transcript');
        const secs = payload.duration_seconds;
        toast.success(
          `Разбор вопроса готов${secs ? ` (${Math.round(secs)} с)` : ''}`,
          { id: "question-consilium" }
        );
      } else if (outcome === 'failed') {
        toast.error(`Консилиум не отработал: ${payload?.error || "причина не названа"}`,
                    { id: "question-consilium" });
      } else if (outcome === 'unknown') {
        /* 🔴 Это НЕ «провалилось» и НЕ «идёт». Состояние работы неизвестно. */
        const why = payload?.reason === 'worker_silent'
          ? "исполнитель молчит дольше предельного времени"
          : "запись о работе не найдена";
        toast.error(
          `Состояние работы неизвестно: ${why}. Справка могла быть записана в свод — ${payload?.fallback || "<тенант>/katifi-writes/questions/"}`,
          { id: "question-consilium", duration: 15000 }
        );
      } else if (outcome === 'gave_up') {
        toast(
          "Ждём дольше 10 минут — опрос остановлен. Работа могла продолжиться; справка ляжет в свод. Откройте страницу позже.",
          { id: "question-consilium", duration: 15000 }
        );
      } else {
        /* cancelled — ушли со страницы, говорить некому. */
        toast.dismiss("question-consilium");
      }
    } catch (err) {
      console.error("Question consilium error:", err);
      toast.error(`Консилиум по вопросу не отработал — ${err.message}`, { id: "question-consilium" });
    } finally {
      setIsConsiliumRunning(false);
    }
  };

  /* Что лежит в браузере и ещё не перенесено. Записи только ЧИТАЮТСЯ. */
  useEffect(() => {
    let migratedAt = null;
    try {
      migratedAt = localStorage.getItem(MIGRATED_MARKER_KEY);
    } catch (e) {
      console.warn("Маркер переноса не прочитан", e);
    }
    if (migratedAt) return;
    setLocalDocketItems(readLocalDocket());
  }, []);

  const humanLocalItems = localDocketItems.filter(
    item => item && !DEMO_CASE_IDS.includes(item.id)
  );
  const demoLocalItemsCount = localDocketItems.length - humanLocalItems.length;

  /* Одноразовый перенос. На сервер уходят ВСЕ записи, включая демо: отбор делает
     бэкенд, чтобы рубеж был один. Повтор безопасен — дубли отсекает БД. */
  const handleMigrateLocalDocket = async () => {
    setIsMigrating(true);
    setMigrationError(null);
    const stamp = new Date().toISOString();
    try {
      const result = await apiService.makeRequest('/ai-paralegal/docket/import', {
        method: 'POST',
        body: JSON.stringify({ items: localDocketItems })
      });
      setMigrationResult(result);
      /* 🔴 Сначала архив, потом маркер, и НИКОГДА removeItem: исходный ключ
         остаётся нетронутым на случай, если перенос окажется неверным. */
      try {
        localStorage.setItem(
          `${ARCHIVE_KEY_PREFIX}_${stamp}`,
          JSON.stringify(localDocketItems)
        );
        localStorage.setItem(MIGRATED_MARKER_KEY, stamp);
      } catch (e) {
        console.warn("Архив переноса не записан", e);
      }
      setLocalDocketItems([]);
      await loadDocket({ silent: true });
      toast.success(`Перенесено дел: ${result?.counts?.imported ?? 0}`);
    } catch (e) {
      console.error("Перенос не выполнен", e);
      // Плашка остаётся на месте: перенос не состоялся, повторить можно.
      setMigrationError(e.message || "причина не названа");
      toast.error(`Перенос не выполнен: ${e.message}`);
    } finally {
      setIsMigrating(false);
    }
  };

  // Какие модели заявлены в конфигурации бэкенда. Один раз при монтировании,
  // без probe: probe тратит платные вызовы, здесь нужна только подпись бейджа.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/v1/ai-paralegal/status');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setAgentProviders(data.agent_providers || null);
          setSupportedPractices(data.supported_practice_areas || []);
        }
      } catch (e) {
        console.warn("Provider status unavailable", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Модель для бейджа в шапке. Неизвестна — показываем прочерк, а не старый
  // литерал: подпись «Claude 3.7» под моделью, отвечающей 404, хуже пустоты.
  const providerModel = (key) => agentProviders?.[key]?.model || '—';

  // Current active case object
  const activeCase = cases.find(c => c.id === activeCaseId) || cases[0] || {};

  /* Русское название практики. Список приходит из /status (supported_practice_areas):
     зашитый на фронте перечень разошёлся бы с реестром бэкенда молча.
     Неизвестная или пустая практика — «Практика не выбрана», не выдуманное название. */
  const practiceLabel = (key) => {
    if (!key) return 'Практика не выбрана';
    if (supportedPractices.length && !supportedPractices.includes(key)) {
      return `Практика «${key}» не заведена в системе`;
    }
    return PRACTICE_TITLES_RU[key] || key;
  };

  /* 🔴 ЗАСТАВЫ БОЛЬШЕ НЕТ (260907-w8j). Обязательный выбор практики оказался не
     той формой: 08.09.2026 вопрос про re-parole всё равно ушёл под чужой
     практикой, потому что селектор сохранил выбор от прошлого вопроса, а ворота
     требовали лишь НЕПУСТОГО значения, не ВЕРНОГО. Практику определяет система.

     🔴 Снятие ворот НЕ возвращает умолчание: пустое значение означает
     «ОПРЕДЕЛИ», а не «подставь самую частую». В JSON уезжает
     `selectedPractice || null`, а в FormData поле не добавляется ВОВСЕ, когда
     практика пуста: пустая строка в multipart приехала бы на сервер как '' и
     дала бы 400 там, где человек просто не выбирал.

     Что показала система, определив практику. Явный выбор человека ГЛАВНЕЕ:
     `practiceChosenByHuman` не даёт определению затереть выбор ни при каком
     исходе. */
  const applyInference = (inference) => {
    if (!inference || typeof inference !== 'object') return;
    setInferredPractice(inference);
    if (inference.status === 'resolved' && inference.key && !practiceChosenByHuman) {
      setSelectedPractice(inference.key);
    }
  };

  // Synchronize active case selection with practice & context
  useEffect(() => {
    if (!activeCaseId) return;
    const current = cases.find(c => c.id === activeCaseId);
    if (!current) return;

    if (current.practiceArea) {
      setSelectedPractice(current.practiceArea);
    }
    setCaseContext(current.caseContext || '');

    /* 🔴 Автоподстановки «образцового документа» больше нет. Выдуманный экспонат
       уходил в консилиум и участвовал в сверке чек-листа как ПРИЛОЖЕННЫЙ документ,
       то есть повышал готовность дела текстом, которого не существует.
       Анализируются только реально загруженные файлы. */
    setSelectedFiles([]);

    setConsiliumResults(null);
    setAttorneyNotes('');
    /* 🔴 Подстановки здесь больше НЕТ. У дела не проставлена практика — чек-лист
       НЕ запрашивается вовсе: чужой чек-лист выглядит как свой, и адвокат сверяет
       дело по требованиям другой петиции, не видя подмены. */
    if (current.practiceArea) {
      fetchChecklist(current.practiceArea, current.caseContext || '');
    } else {
      setChecklistData(null);
    }
    /* 🔴 Зависимость ТОЛЬКО от activeCaseId. С `cases` в списке эффект срабатывал бы
       и на перечитывание докета после консилиума — и тут же стирал setConsiliumResults,
       то есть заключение исчезало бы с экрана сразу после того, как за него заплатили.
       `cases` здесь свежий: setCases и setActiveCaseId в loadDocket попадают в один
       рендер, а без смены дела сбрасывать нечего. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCaseId]);

  // Fetch USCIS Checklist from Backend
  const fetchChecklist = async (practiceKey, contextStr, caseIdOverride) => {
    setIsLoadingChecklist(true);
    const caseIdToSend = caseIdOverride !== undefined ? caseIdOverride : activeCaseId;
    try {
      const res = await fetch('/api/v1/ai-paralegal/checklist/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          legal_area: practiceKey,
          case_details: contextStr,
          case_id: caseIdToSend ?? null
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setChecklistData(data);
    } catch (err) {
      console.error("Checklist error:", err);
      toast.error("Не удалось загрузить чек-лист USCIS");
    } finally {
      setIsLoadingChecklist(false);
    }
  };

  // Handle Practice Change
  const handlePracticeChange = (newPractice) => {
    setSelectedPractice(newPractice);
    /* 🔴 Признак «выбрал человек» взводится и СНИМАЕТСЯ: возврат к пустому
       значению — это осознанный возврат к «определи», а не потеря выбора. */
    setPracticeChosenByHuman(Boolean(newPractice));
    /* Пустой ключ — не запрос «дай чек-лист ничего»: сервер обязан молчать. */
    if (newPractice) {
      fetchChecklist(newPractice, caseContext);
    } else {
      setChecklistData(null);
    }
  };

  // Handle File Uploads
  const handleFilesAdded = (filesList) => {
    const arr = Array.from(filesList);
    setSelectedFiles(prev => [...prev, ...arr]);
    toast.success(`Добавлено файлов: ${arr.length}`);
  };

  const removeFile = (idx) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Run Consilium (with optional attorney feedback override)
  const runConsilium = async (customAttorneyNotes = null) => {
    /* 🔴 Заставы по практике здесь НЕТ (260907-w8j). Обе отправки —
       upload-and-analyze и documents/analyze — уходят с практикой из одного и
       того же состояния, и пустое значение в обеих означает «ОПРЕДЕЛИ». */
    setIsConsiliumRunning(true);
    const notesToSend = customAttorneyNotes !== null ? customAttorneyNotes : attorneyNotes;
    const hasNotes = Boolean(notesToSend && notesToSend.trim());

    toast.loading(
      hasNotes 
        ? "Адвокаты пересматривают дело с учетом указаний Екатерины..." 
        : "Иммиграционный консилиум начинает работу...", 
      { id: "consilium-run" }
    );

    try {
      let result = null;
      /* Длительность прогона. Раньше её никто не мерил вовсе; теперь её меряет
         сам учёт работ и отдаёт в ответе опроса. null = мерить было нечем
         (синхронный путь upload-and-analyze). */
      let jobDurationSeconds = null;
      const hasRealFiles = selectedFiles.some(f => f instanceof File);

      if (hasRealFiles) {
        const formData = new FormData();
        /* 🔴 Поле НЕ добавляется вовсе, когда практика пуста: пустая строка в
           multipart приехала бы как '' и попала бы в закрытый перечень как ключ.
           Отсутствие поля читается сервером как «определи по фабуле». */
        if (selectedPractice) {
          formData.append("legal_area", selectedPractice);
        }
        formData.append("case_context", caseContext);
        // Итог прогона записывается в состояние ИМЕННО этого дела.
        if (activeCaseId !== null && activeCaseId !== undefined) {
          formData.append("case_id", String(activeCaseId));
        }
        if (hasNotes) {
          formData.append("attorney_notes", notesToSend.trim());
        }
        selectedFiles.forEach(f => {
          if (f instanceof File) {
            formData.append("files", f);
          }
        });

        const res = await fetch('/api/v1/ai-paralegal/documents/upload-and-analyze', {
          method: 'POST',
          body: formData
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        result = await res.json();
        applyInference(result?.practice_inference);
      } else {
        const docs = selectedFiles.map(f => ({
          filename: f.name || "intake_case_notes.txt",
          text: f.text || caseContext || "Case materials"
        }));

        if (docs.length === 0) {
          docs.push({
            filename: "intake_case_notes.txt",
            text: caseContext || "Case materials"
          });
        }

        const res = await fetch('/api/v1/ai-paralegal/documents/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            legal_area: selectedPractice || null,
            case_context: caseContext,
            attorney_notes: hasNotes ? notesToSend.trim() : null,
            case_id: activeCaseId ?? null,
            documents: docs
          })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        /* 202 + идентификатор работы: консилиум идёт вне запроса, соединение
           не висит минутами. Дальше — опрос раз в 5 с, не дольше 10 минут. */
        const accepted = await res.json();
        if (!accepted?.job_id) {
          throw new Error("сервер принял запрос, но идентификатора работы не выдал");
        }
        applyInference(accepted.practice_inference);

        const { outcome, payload } = await pollConsiliumJob({
          jobId: accepted.job_id,
          fetchImpl: (url) => fetch(url),
          isCancelled: () => pollCancelRef.current,
        });

        /* Пять исходов — пять РАЗНЫХ сообщений. Продолжаем разбор только на done. */
        if (outcome === 'failed') {
          toast.error(`Консилиум не отработал: ${payload?.error || "причина не названа"}`,
                      { id: "consilium-run" });
          return;
        }
        if (outcome === 'unknown') {
          /* 🔴 Это НЕ «провалилось» и НЕ «идёт». */
          const why = payload?.reason === 'worker_silent'
            ? "исполнитель молчит дольше предельного времени"
            : "запись о работе не найдена";
          toast.error(
            `Состояние работы неизвестно: ${why}. Досье могло быть записано в свод — ${payload?.fallback || "<тенант>/katifi-writes/cases/"}`,
            { id: "consilium-run", duration: 15000 }
          );
          return;
        }
        if (outcome === 'gave_up') {
          toast(
            "Ждём дольше 10 минут — опрос остановлен. Работа могла продолжиться; досье ляжет в свод. Откройте страницу позже.",
            { id: "consilium-run", duration: 15000 }
          );
          return;
        }
        if (outcome === 'cancelled') {
          toast.dismiss("consilium-run");
          return;
        }
        /* Форма result — ровно прежняя, поэтому весь разбор ниже не меняется:
           переехал транспорт, а не отрисовка. */
        result = payload.result;
        setLastJobId(accepted.job_id);
        jobDurationSeconds = payload.duration_seconds ?? null;
      }

      setConsiliumResults(result);
      /* Оба пути по делу — синхронный и через очередь — приносят исход
         определения. Показываем его от ИТОГА тоже: 202-ответ мог не дойти. */
      applyInference(result?.practice_inference);

      // Ни один файл не теряется молча: отвергнутое видно сразу, а не только в логах бэкенда.
      const skipped = result?.skipped_files || [];
      if (skipped.length > 0) {
        toast.error(
          `Файлов не принято: ${skipped.length}. Причины — во вкладке «Gap Analysis».`,
          { duration: 9000 }
        );
      }

      if (hasNotes) {
        setAttorneyDirectivesLog(prev => [
          ...prev,
          { case: activeCase.caseNumber || activeCase.title, note: notesToSend.trim(), timestamp: new Date().toISOString() }
        ]);
      }

      // Деградировавший консилиум не празднует успех: тост об ошибке, а не toast.success
      const consilium = result?.multi_agent_consilium;
      const isDegraded = consilium?.status === "DEGRADED";
      if (isDegraded) {
        const dead = (consilium.degraded_agents || []).join(", ") || "часть агентов";
        toast.error(
          `Консилиум неполный: не отработали ${dead}. Заключение содержит пробелы.`,
          { id: "consilium-run" }
        );
      } else {
        /* Длительность приписывается только там, где она ИЗМЕРЕНА. Отсутствие
           числа — это «не мерили», а не «мгновенно». */
        const took = jobDurationSeconds ? ` (${Math.round(jobDurationSeconds)} с)` : '';
        toast.success(
          hasNotes
            ? `Консилиум пересмотрел дело по указаниям адвоката${took}`
            : `Консилиум сформировал заключение${took}`,
          { id: "consilium-run" }
        );
      }

      /* 🔴 Провал записи в докет обязан быть виден: прогон состоялся и деньги
         потрачены, а состояние дела не обновилось. */
      if (result?.state_write_error) {
        toast.error(
          `Итог консилиума НЕ записан в докет: ${result.state_write_error}`,
          { duration: 12000 }
        );
      }

      /* Список перечитывается с сервера, а не правится локально: экран обязан
         показывать то, что записано в БД, иначе докет разойдётся с фактами. */
      await loadDocket({ silent: true });

    } catch (err) {
      console.error("Consilium error:", err);
      toast.error(`Ошибка консилиума: ${err.message}`, { id: "consilium-run" });
    } finally {
      setIsConsiliumRunning(false);
    }
  };

  /* ─── СПРАВКА КОНСИЛИУМА ───────────────────────────────────────────────
     🔴 Оба обращения идут ПО ИДЕНТИФИКАТОРУ работы. Путь наружу не передаётся и
     сервером не принимается: иначе угаданный идентификатор превратился бы в
     чтение произвольного файла контейнера.
     🔴 Отказ сервера показывается ДОСЛОВНО, вместе с машиночитаемой причиной:
     «указателя нет», «файла на диске нет» и «справка не записана» — три разные
     новости, и на экране они обязаны читаться по-разному. Один общий текст
     «не удалось» вернул бы ровно ту болезнь, что здесь чинилась. */
  const briefFailure = async (res) => {
    let detail = null;
    try { detail = (await res.json())?.detail; } catch (e) { detail = null; }
    if (detail && typeof detail === 'object') {
      return `${detail.message || 'причина не названа'} [${detail.reason || 'без кода'}]`;
    }
    return `${detail || `HTTP ${res.status}`}`;
  };

  const openBrief = async () => {
    if (!lastJobId) return;
    setIsBriefLoading(true);
    try {
      const res = await fetch(`/api/v1/ai-paralegal/consilium/jobs/${lastJobId}/brief`);
      if (!res.ok) throw new Error(await briefFailure(res));
      const data = await res.json();
      /* Переиспользуем существующую модалку отчёта Катифи: вторая модалка с тем
         же назначением разъехалась бы с первой по оформлению и поведению. */
      setKatifiReportText(data.markdown || '');
      setObsidianPath(data.rel_path || '');
      setIsReportModalOpen(true);
    } catch (err) {
      toast.error(`Справка не открыта: ${err.message}`, { duration: 15000 });
    } finally {
      setIsBriefLoading(false);
    }
  };

  const downloadBriefDocx = async () => {
    if (!lastJobId) return;
    setIsBriefLoading(true);
    try {
      const res = await fetch(`/api/v1/ai-paralegal/consilium/jobs/${lastJobId}/brief.docx`);
      if (!res.ok) throw new Error(await briefFailure(res));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'spravka-konsiliuma.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`DOCX не выгружен: ${err.message}`, { duration: 15000 });
    } finally {
      setIsBriefLoading(false);
    }
  };

  // Katifi Daily Reporting
  const handleOpenKatifiReport = async () => {
    setIsReportModalOpen(true);
    if (!katifiReportText) {
      try {
        const res = await fetch('/api/v1/ai-paralegal/katifi/daily-report');
        if (res.ok) {
          const data = await res.json();
          if (data.has_report) {
            setKatifiReportText(data.report_markdown);
            return;
          }
        }
      } catch (e) {
        console.warn("Fetch report error", e);
      }
      generateKatifiReport();
    }
  };

  const generateKatifiReport = async () => {
    setIsGeneratingReport(true);
    try {
      const res = await fetch('/api/v1/ai-paralegal/katifi/daily-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* 🔴 Список дел больше НЕ отправляется: с 06.09.2026 отчёт строится по
           докету ИЗ БАЗЫ, тем же правилом видимости, что и раздел «Дела». Отчёт
           фирмы ложится в базу знаний и читается как документ — он не может
           зависеть от того, что собрал браузер. Прежний переходник вдобавок
           закрывал известный долг собой: репортёр подставлял 0 % и false там,
           где ключа не было, и «сверки не проводилось» становилось «ноль
           процентов готовности». Теперь эти числа считаются на сервере. */
        body: JSON.stringify({
          attorney_directives: attorneyDirectivesLog
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setKatifiReportText(data.report_markdown);
      setObsidianPath(data.obsidian_path || null);
      if (data.success === true) {
        toast.success("Ежедневный отчет Katifi сохранен в Obsidian!");
      } else {
        toast.error(`Отчет сформирован, но НЕ сохранен в Obsidian: ${data.error || "причина не названа"}`);
      }
    } catch (e) {
      console.error("Generate report error", e);
      toast.error(`Ошибка генерации отчета: ${e.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  /* 🔴 УБРАНА КНОПКА «ЗАПОЛНИТЬ ДЕМО-ДАННЫМИ» (15.09.2026, quick-260915-did).
     Она наливала в форму НАСТОЯЩЕГО дела целую анкету несуществующего человека:
     ФИО, гражданство, A-Number, телефон, супругу, дату и место брака, дату
     въезда и порт. Тост «это не реальный клиент» дефекта не снимал: после
     нажатия НА ЭКРАНЕ оставались готовые к отправке поля, отличить которые от
     введённых рукой было нечем, а `modalContact` уходит на сервер как
     `client_phone_number` — тот самый признак, по которому клиент опознаётся.
     Выдуманный номер в опознавателе клиента — это не демонстрация, это порча
     докета.

     Подсказки остаются ТОЛЬКО в `placeholder`, и образец там очевидно
     ненастоящий (`A# 000-000-000`, `+1 (000) 000-0000`): placeholder не
     попадает ни в состояние формы, ни на сервер. */

  // Submit New Case
  const handleCreateCase = async () => {
    if (!modalClientName.trim()) {
      toast.error("Пожалуйста, укажите ФИО заявителя");
      return;
    }
    if (!modalClientFirstName.trim()) {
      toast.error("Нужны имя клиента и хотя бы один контакт: почта или телефон");
      return;
    }
    /* 🔴 Опознаватель клиента — почта ИЛИ телефон. Требовать почту безусловно
       значило отбрасывать на входе клиента, у которого есть только номер. */
    if (!modalClientEmail.trim() && !modalContact.trim()) {
      toast.error("Нужны имя клиента и хотя бы один контакт: почта или телефон");
      return;
    }

    const displayName = modalSpouseName.trim()
      ? `${modalClientName.trim()} & ${modalSpouseName.trim()}`
      : modalClientName.trim();

    let compiledContext = modalCaseContext.trim();
    const details = [];
    if (modalCitizenship) details.push(`Гражданство: ${modalCitizenship}`);
    if (modalANumber) details.push(`A-Number: ${modalANumber}`);
    if (modalSpouseName) details.push(`Супруг/Петиционер: ${modalSpouseName} (${modalSpouseStatus}), брак: ${modalMarriageDetails || 'Да'}`);
    if (modalEntryDate) details.push(`Дата въезда в США: ${modalEntryDate} через ${modalPortOfEntry || 'порт не указан'} (${modalClassOfAdmission})`);
    if (modalI94Date || modalI94Ds) details.push(`I-94: ${modalI94Ds ? 'D/S' : modalI94Date}`);
    if (modalFilingDeadline) details.push(`Дедлайн: ${modalFilingDeadline}`);
    if (riskFlags.overstay) details.push(`[ВНИМАНИЕ: Overstay / Unlawful Presence]`);
    if (riskFlags.priorDenials) details.push(`[ВНИМАНИЕ: Предыдущие отказы USCIS/посольства]`);
    if (riskFlags.criminal) details.push(`[ВНИМАНИЕ: Наличие арестов/приводов]`);
    if (riskFlags.jointSponsor) details.push(`[Требуется Joint Sponsor I-864]`);

    if (details.length > 0) {
      compiledContext += `\n\nПараметры въезда и статуса:\n• ` + details.join('\n• ');
    }

    /* 🔴 Номер дела здесь НЕ выдумывается. Он приходит от сервера из существующей
       генерации (IM-YYYY-NNNNNN). Прежний самодельный номер со случайными цифрами
       был вторым, ни с чем не связанным рядом, которого не знала ни одна другая
       часть системы.
       «Образцовый документ» тоже больше не сочиняется: выдуманный текст уходил в
       консилиум как приложенный экспонат дела. */
    setIsCreatingCase(true);
    try {
      const row = await apiService.makeRequest('/ai-paralegal/docket', {
        method: 'POST',
        body: JSON.stringify({
          title: displayName,
          client_email: modalClientEmail.trim() || null,
          client_first_name: modalClientFirstName.trim(),
          client_last_name: modalClientLastName.trim() || null,
          client_phone_number: modalContact.trim() || null,
          practice_area: modalCategory,
          case_context: compiledContext
        })
      });
      // Источник правды — сервер: список перечитывается, а не дописывается локально.
      await loadDocket({ silent: true });
      setActiveCaseId(row?.case_id ?? null);
      setIsModalOpen(false);
      toast.success(`Дело ${row?.case_number || ''} (${displayName}) открыто`);
    } catch (e) {
      console.error("Дело не создано", e);
      // Дело в список НЕ добавляется: на сервере его нет.
      toast.error(`Дело не создано: ${e.message}`);
    } finally {
      setIsCreatingCase(false);
    }
  };

  const filteredCases = cases.filter(c => {
    const needle = searchFilter.toLowerCase();
    if (!needle) return true;
    return [c.title, c.caseNumber, practiceLabel(c.practiceArea)]
      .some(v => (v || '').toLowerCase().includes(needle));
  });

  return (
    <div className="ai-paralegal-container fk-page fk-ap flex flex-col gap-4">

      {/* ШАПКА ЭКРАНА — УЗОР ОБЩЕГО СЛОЯ, а не своя разметка (quick-260912-23z).
          Третья форма шапки (`ShapkaSKnopkoy`): главное действие ЕСТЬ и оно
          КНОПКА НА МЕСТЕ — открывает окно создания дела. Ссылкой его выразить
          нечем: маршрута у создания дела нет, и `to` пришлось бы выдумать.
          Прежняя кнопка «Новое дело» стояла внутри карточки списка дел; она
          убрана оттуда, а не продублирована: двух главных действий у экрана
          быть не может. */}
      <ShapkaSKnopkoy
        nadzagolovok="AI IMMIGRATION OPERATING SYSTEM"
        zagolovok="FITENKO LAW"
        podstroka="Интеллектуальный иммиграционный консилиум"
        podpis="Новое дело"
        naZhatie={() => setIsModalOpen(true)}
      />

      {/* 🔴 ЕДИНСТВЕННАЯ ТЁМНАЯ ПОЛОСА ЭКРАНА (решение владельца Д-02): ряд пяти
          агентов консилиума. Всё остальное на экране светлое. Цвет полосы —
          токеном `--council-bg`, одним правилом в AIParalegal.css; текст внутри
          адресуется ПО ТЕГУ и по СВОИМ классам этого экрана, но НИ ОДНИМ именем
          класса общего слоя: правило, назвавшее класс слоя, стало бы вторым
          держателем его оформления. */}
      <div className="ap-konsilium">
        <div className="ap-agent">
          <div className="ap-agent-znak ap-agent-znak-vedushchiy">ЕФ</div>
          <div>
            <div className="ap-agent-strochka">
              <span className="ap-agent-imya">Екатерина Фитенко</span>
              <span className="ap-agent-metka">Lead Attorney</span>
            </div>
            <p className="ap-agent-rol">Адвокат-партнер (Human)</p>
          </div>
        </div>

        <div className="ap-agent">
          <div className="ap-agent-znak ap-agent-znak-elena">ЕР</div>
          <div>
            <div className="ap-agent-strochka">
              <span className="ap-agent-imya">Елена</span>
              <span title="Заявлено в конфигурации. Кто ответил фактически — в карточке агента в протоколе." className="ap-agent-model font-mono">{providerModel('gemini')}</span>
            </div>
            <p className="ap-agent-rol">Сбор фактов (Discovery)</p>
          </div>
        </div>

        <div className="ap-agent">
          <div className="ap-agent-znak ap-agent-znak-aleksandr">АВ</div>
          <div>
            <div className="ap-agent-strochka">
              <span className="ap-agent-imya">Александр</span>
              <span title="Заявлено в конфигурации. Кто ответил фактически — в карточке агента в протоколе." className="ap-agent-model font-mono">{providerModel('claude')}</span>
            </div>
            <p className="ap-agent-rol">Аудит рисков (Senior Counsel)</p>
          </div>
        </div>

        <div className="ap-agent">
          <div className="ap-agent-znak ap-agent-znak-markus">МС</div>
          <div>
            <div className="ap-agent-strochka">
              <span className="ap-agent-imya">Маркус</span>
              <span title="Заявлено в конфигурации. Кто ответил фактически — в карточке агента в протоколе." className="ap-agent-model font-mono">{providerModel('openai')}</span>
            </div>
            <p className="ap-agent-rol">Юр. операции (Legal Ops)</p>
          </div>
        </div>

        <div className="ap-agent">
          <div className="ap-agent-znak ap-agent-znak-katifi">КФ</div>
          <div>
            <div className="ap-agent-strochka">
              <span className="ap-agent-imya">Катифи</span>
              <span title="Заявлено в конфигурации. Кто ответил фактически — в карточке агента в протоколе." className="ap-agent-model font-mono">{providerModel('katifi')}</span>
            </div>
            <p className="ap-agent-rol">База знаний (Obsidian)</p>
          </div>
        </div>

        <button
          onClick={handleOpenKatifiReport}
          className="ap-konsilium-act"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>Отчет Katifi</span>
        </button>
      </div>

      {/* 3-COLUMN MAIN DASHBOARD */}
      <main className="w-full flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4">

        {/* ========================================================================= */}
        {/* COLUMN 1: СПИСОК ДЕЛ И БАР ПО ВЫПОЛНЕННОЙ РАБОТЕ (3 COLS)                 */}
        {/* ========================================================================= */}
        <div className="lg:col-span-3 flex flex-col gap-4">
          <div className="glass-card p-4 rounded-2xl flex flex-col flex-1">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--line)]">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[var(--gold-ink)]" />
                <h2 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)]">Список иммиграционных дел</h2>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="w-3.5 h-3.5 text-[var(--ink-muted-ink)] absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Поиск клиента или формы..."
                className="w-full bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl pl-8 pr-3 py-1.5 text-xs text-[var(--ink)] placeholder-[var(--ink-muted)] focus:outline-none focus:border-[var(--gold)]"
              />
            </div>

            {/* Плашка одноразового переноса записей, заведённых в этом браузере */}
            {humanLocalItems.length > 0 && (
              <div className="mb-3 p-3 rounded-xl border border-amber-500/40 bg-[var(--gold-soft)]">
                <p className="text-[11px] font-semibold text-[var(--amber-accent-ink)]">
                  Найдено дел, сохранённых только в этом браузере: {humanLocalItems.length}
                </p>
                <p className="text-[10px] text-[var(--amber-accent-ink)] mt-1">
                  Они есть лишь в этой вкладке и не видны никому другому. Перенос заведёт по
                  ним настоящие дела с номерами системы.
                  {demoLocalItemsCount > 0 && (
                    <> Демонстрационных записей ({demoLocalItemsCount}) перенос не затронет —
                    клиент и фабула в них выдуманы.</>
                  )}
                </p>
                <p className="text-[10px] text-[var(--amber-accent-ink)] mt-1">
                  Исходные данные в браузере сохраняются: ничего не удаляется.
                </p>
                {migrationError && (
                  <p className="text-[10px] text-[var(--red-accent-ink)] mt-1.5">
                    Перенос не выполнен: {migrationError}. Повторить безопасно — дубли
                    отсекаются на сервере.
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleMigrateLocalDocket}
                  disabled={isMigrating}
                  className="mt-2 gold-button-outline text-xs px-3 py-1 rounded-lg font-semibold"
                >
                  {isMigrating ? "Переносим..." : "Перенести в систему"}
                </button>
              </div>
            )}

            {/* Разбор переноса: принято, отклонено и почему, что пропущено как дубль */}
            {migrationResult && (
              <div className="mb-3 p-3 rounded-xl border border-[var(--line)] bg-[var(--surface)] text-[10px] space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-[var(--gold-ink)]">Итог переноса</span>
                  <button
                    type="button"
                    onClick={() => setMigrationResult(null)}
                    className="text-[var(--ink-muted-ink)] hover:text-[var(--ink)]"
                  >
                    Скрыть
                  </button>
                </div>
                <p className="text-[var(--ink)]">
                  Прислано: {migrationResult.counts?.received ?? '—'} · перенесено:{' '}
                  {migrationResult.counts?.imported ?? '—'} · отклонено:{' '}
                  {migrationResult.counts?.rejected ?? '—'} · дублей:{' '}
                  {migrationResult.counts?.duplicates ?? '—'}
                </p>
                {(migrationResult.rejected || []).map((r, i) => (
                  <p key={`rej-${i}`} className="text-[var(--red-accent-ink)]">
                    Отклонено {r.id}: {r.reason}
                  </p>
                ))}
                {(migrationResult.skipped_duplicates || []).map((r, i) => (
                  <p key={`dup-${i}`} className="text-[var(--ink-muted-ink)]">
                    Пропущено {r.id}: {r.reason}
                  </p>
                ))}
                {(migrationResult.warnings || []).map((w, i) => (
                  <p key={`warn-${i}`} className="text-[var(--amber-accent-ink)]">{w}</p>
                ))}
              </div>
            )}

            {/* 🔴 Ошибка загрузки НЕ рисуется как «дел нет»: отказ измерителя — не ноль */}
            {docketError && (
              <div className="mb-3 p-3 rounded-xl border border-rose-500/40 bg-[var(--surface-soft)]">
                <p className="text-[11px] font-semibold text-[var(--red-accent-ink)]">
                  Не удалось загрузить докет: {docketError}
                </p>
                <button
                  type="button"
                  onClick={() => loadDocket()}
                  disabled={isDocketLoading}
                  className="mt-2 gold-button-outline text-xs px-3 py-1 rounded-lg font-semibold"
                >
                  {isDocketLoading ? "Загружаем..." : "Повторить"}
                </button>
              </div>
            )}

            {/* Cases List */}
            <div className="space-y-2 overflow-y-auto max-h-[380px] pr-1 flex-1">
              {filteredCases.map(c => {
                const isActive = c.id === activeCaseId;
                return (
                  <div
                    key={c.id}
                    onClick={() => setActiveCaseId(c.id)}
                    className={`p-2.5 rounded-xl border cursor-pointer transition relative ${
                      isActive ? 'case-item-active' : 'bg-[var(--surface)] border-[var(--line)] hover:border-[var(--line)]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      {/* Настоящий номер дела из cases, а не выдуманный ряд */}
                      <span className="font-mono text-[10px] text-[var(--gold-ink)] font-bold">{c.caseNumber}</span>
                      <span className="text-[9px] px-1.5 py-0.2 rounded border font-medium bg-[var(--surface-soft)] text-[var(--ink-muted-ink)] border-[var(--line)]">
                        {c.status || '—'}
                      </span>
                    </div>
                    <h3 className="font-semibold text-xs text-[var(--ink)] truncate">{c.title}</h3>
                    <p className="text-[10px] text-[var(--ink-muted-ink)] mt-0.5">{practiceLabel(c.practiceArea)}</p>

                    {/* Прочерк = не считали. Ноль = посчитали и вышел ноль. */}
                    <div className="mt-2 flex items-center justify-between text-[9px] text-[var(--ink-muted-ink)]">
                      <span>Приложено: {pctLabel(c.attachedPercentage)}</span>
                      <span className="font-mono text-[var(--gold-ink)]">{pctLabel(c.completionPercentage)}</span>
                    </div>
                    <div className="w-full bg-[var(--surface-soft)] h-1 rounded-full mt-1 overflow-hidden">
                      <div
                        className={`h-1 rounded-full transition-all duration-300 ${
                          c.completionPercentage === null || c.completionPercentage === undefined
                            ? 'bg-[var(--line)]'
                            : 'bg-[var(--gold)]'
                        }`}
                        style={{ width: `${pctWidth(c.completionPercentage)}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Честное пустое состояние: почему пусто, а не просто «пусто» */}
              {docketLoaded && !docketError && filteredCases.length === 0 && (
                <div className="text-center py-10 text-[var(--ink-muted-ink)]">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40 text-[var(--gold-ink)]" />
                  {searchFilter ? (
                    <p className="text-[11px]">По запросу «{searchFilter}» дел не найдено</p>
                  ) : visibilityScope === 'assigned' ? (
                    <Sostoyanie tekst="Вам не назначено ни одного дела" />
                  ) : (
                    <Sostoyanie tekst="Докет пуст: в системе ещё нет заведённых дел" />
                  )}
                  <p className="text-[10px] mt-1 text-[var(--ink-muted-ink)]">
                    Демонстрационных дел здесь больше нет — список показывает только
                    настоящие дела фирмы.
                  </p>
                </div>
              )}

              {!docketLoaded && !docketError && (
                <div className="text-center py-10 text-[var(--ink-muted-ink)]">
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-[var(--gold-ink)]" />
                  <p className="text-[11px]">Загружаем докет...</p>
                </div>
              )}
            </div>

            {/* WORK PROGRESS BAR DIRECTLY UNDERNEATH */}
            <div className="mt-4 pt-3 border-t border-[var(--line)]">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-[var(--ink-muted-ink)] flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5 text-[var(--gold-ink)]" /> Готовность дела:
                </span>
                <span className="font-mono font-bold text-[var(--gold-ink)]">{pctLabel(activeCase.completionPercentage)}</span>
              </div>
              <div className="w-full bg-[var(--surface-soft)] h-2 rounded-full overflow-hidden border border-[var(--line)]">
                <div
                  className={`h-2 rounded-full transition-all duration-500 shadow-sm ${
                    activeCase.completionPercentage === null || activeCase.completionPercentage === undefined
                      ? 'bg-[var(--line)]'
                      : 'bg-[var(--gold)]'
                  }`}
                  style={{ width: `${pctWidth(activeCase.completionPercentage)}%` }}
                />
              </div>
              <p className="text-[10px] text-[var(--ink-muted-ink)] mt-2 flex items-center justify-between">
                {/* Пусто = действий по делу ещё не было; выдуманное «Готово к консилиуму» убрано */}
                <span>{activeCase.lastAction || '—'}</span>
                {/* Три состояния готовности, а не два */}
                <span className="text-[var(--gold-ink)] font-medium">{readinessLabel(activeCase.isReadyForFiling)}</span>
              </p>
            </div>

          </div>
        </div>

        {/* ========================================================================= */}
        {/* COLUMN 2: ПРАКТИКА, ЧЕК-ЛИСТ USCIS И ЗАГРУЗКА ДОКУМЕНТОВ (4 COLS)          */}
        {/* ========================================================================= */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <div className="glass-card p-4 rounded-2xl flex flex-col flex-1">
            
            {/* Practice Selector */}
            <div className="mb-3">
              <label className="block font-serif-legal font-bold text-xs text-[var(--gold-ink)] mb-1.5">
                Категория иммиграционной петиции USCIS
              </label>
              <select
                value={selectedPractice}
                onChange={(e) => handlePracticeChange(e.target.value)}
                className="w-full bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)] focus:outline-none focus:border-[var(--gold)]"
              >
                {/* 🔴 Ключи берутся из реестра бэкенда (/status). Прежний зашитый список
                    содержал citizenship_n400, которого в реестре НЕТ вовсе: он лишь
                    случайно ловился маршрутизацией по ключевым словам. */}
                {/* 🔴 Первой всегда пустая опция. Прежняя заглушка подставляла
                    value={selectedPractice}, то есть «выбор» был уже сделан за
                    адвоката, и отправка уходила под практикой, которую никто не
                    выбирал. */}
                {/* 🔴 Пустая опция ОСТАЁТСЯ и меняет только смысл: без неё
                    вернуться к «определи» после ручного выбора было бы нечем.
                    В модальном окне заведения дела подпись НЕ меняется:
                    определения там нет, и обещать его было бы ложью. */}
                <option value="">— определить автоматически по тексту —</option>
                {supportedPractices.map(key => (
                  <option key={key} value={key}>{PRACTICE_TITLES_RU[key] || key}</option>
                ))}
                {supportedPractices.length === 0 && (
                  <option value="" disabled>Список практик не загружен</option>
                )}
              </select>

              {/* 🔴 ЧЕТЫРЕ ИСХОДА — ЧЕТЫРЕ РАЗНЫЕ СТРОКИ. Одна строка на все
                  исходы схлопнула бы их обратно, а различить «определена»,
                  «неоднозначно», «не определена» и «выбрали вы» — вся суть
                  правки: подставленное молча и определённое по вопросу выглядят
                  одинаково ровно до тех пор, пока об этом не сказано словами. */}
              {inferredPractice && (
                <p className="mt-1.5 text-[10.5px] leading-snug text-[var(--ink-muted-ink)]">
                  {inferredPractice.status === 'human_choice' && (
                    <span>Практику выбрали вы — определение не запрашивалось.</span>
                  )}
                  {inferredPractice.status === 'resolved' && (
                    <span>
                      Практику определила система: {practiceLabel(inferredPractice.key)}.
                      {' '}Основание: {inferredPractice.basis || 'не названо'}.
                      {' '}Можно переопределить выбором.
                    </span>
                  )}
                  {inferredPractice.status === 'ambiguous' && (
                    <span className="text-[var(--gold-ink)]">
                      Однозначно определить не удалось. Кандидаты:{' '}
                      {(inferredPractice.candidates || []).map(practiceLabel).join(', ') || 'не названы'}.
                      {' '}Выберите сами — иначе разбор пойдёт без ярлыка практики.
                    </span>
                  )}
                  {inferredPractice.status === 'undetermined' && (
                    <span className="text-[var(--gold-ink)]">
                      Практика не определена: {inferredPractice.error || 'причина не названа'}.
                      {' '}Прецеденты искались по фактам вопроса, ярлык не подставлялся.
                    </span>
                  )}
                </p>
              )}
            </div>

            {/* Case Notes / Context */}
            <div className="mb-3">
              <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Фабула дела и вводные данные клиента</label>
              <textarea
                value={caseContext}
                onChange={(e) => setCaseContext(e.target.value)}
                placeholder="Опишите обстоятельства въезда, статус супругов, факты преследования..."
                rows={3}
                className="w-full bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl p-2.5 text-xs text-[var(--ink)] placeholder-[var(--ink-muted)] focus:outline-none focus:border-[var(--gold)] resize-none"
              />
            </div>

            {/* Dynamic USCIS Checklist */}
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-[var(--line)]">
              <div className="flex items-center gap-1.5">
                <ClipboardCheck className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                <h3 className="font-serif-legal font-bold text-xs text-[var(--gold-ink)]">
                  {checklistData?.practice_name_ru || 'Чек-лист документов USCIS'}
                </h3>
              </div>
              {isLoadingChecklist && <Loader2 className="w-3 h-3 animate-spin text-[var(--gold-ink)]" />}
            </div>

            <div className="space-y-2.5 overflow-y-auto max-h-[300px] pr-1 flex-1">
              {checklistData?.items?.map((item, idx) => (
                <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface)] border border-[var(--line)] hover:border-[var(--line)] transition">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 rounded bg-[var(--surface-soft)] text-[var(--gold-ink)] text-[9px] font-mono flex items-center justify-center border border-[var(--line)]">
                        {idx + 1}
                      </span>
                      <h4 className="font-semibold text-xs text-[var(--ink)] line-clamp-1">{item.name}</h4>
                    </div>
                    {item.is_mandatory ? (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.2 rounded bg-[var(--surface-soft)] text-[var(--red-accent-ink)] border border-rose-500/30">
                        Обяз.
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider px-1 py-0.2 rounded bg-[var(--surface-soft)] text-[var(--ink-muted-ink)]">
                        Опц.
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[var(--gold-ink)] font-medium pl-6">{item.name_ru || ''}</p>
                  <div className="mt-1.5 pl-6 text-[10px] text-[var(--ink-muted-ink)] space-y-0.5">
                    <p><strong className="text-[var(--ink)]">Зачем:</strong> {item.description_client}</p>
                    <p><strong className="text-[var(--ink)]">Где взять:</strong> {item.where_to_get}</p>
                    <p><strong className="text-[var(--ink)]">Требование:</strong> <span className="text-[var(--gold-ink)] font-mono text-[9px]">{item.validity_requirements}</span></p>
                  </div>
                </div>
              ))}
            </div>

            {/* Dropzone */}
            <div className="mt-3 pt-3 border-t border-[var(--line)]">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border border-dashed border-[var(--line)] hover:border-[var(--gold)] rounded-xl p-3 text-center cursor-pointer transition bg-[var(--surface-soft)] hover:bg-[var(--surface-soft)]"
              >
                <UploadCloud className="w-5 h-5 text-[var(--gold-ink)] mx-auto mb-1 opacity-80" />
                <p className="text-xs font-semibold text-[var(--ink)]">Загрузить PDF / DOCX / сканы клиента</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.md"
                  onChange={(e) => handleFilesAdded(e.target.files)}
                  className="hidden"
                />
              </div>
              {selectedFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selectedFiles.map((f, i) => (
                    <span key={i} className="text-[10px] bg-[var(--surface-soft)] text-[var(--gold-ink)] px-2 py-0.5 rounded-lg border border-[var(--line)] flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span className="truncate max-w-[140px]">{f.name}</span>
                      <button onClick={() => removeFile(i)} className="text-[var(--ink-muted-ink)] hover:text-[var(--red-accent-ink)] ml-1 font-bold">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ========================================================================= */}
        {/* COLUMN 3: КОНСИЛИУМ АДВОКАТОВ И РЕЗУЛЬТАТЫ (5 COLS)                       */}
        {/* ========================================================================= */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          
          {/* Top Consilium Banner */}
          <div className="glass-card p-3.5 rounded-2xl flex items-center justify-between gap-4">
            <div>
              <h3 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)]">Иммиграционный консилиум Fitenko Law</h3>
              <p className="text-[11px] text-[var(--ink-muted-ink)]">Екатерина Фитенко, Александр, Елена, Маркус, Катифи</p>
            </div>
            <button
              onClick={() => runConsilium(attorneyNotes)}
              disabled={isConsiliumRunning}
              className="gold-button px-4 py-2 rounded-xl flex items-center gap-2 text-xs font-bold whitespace-nowrap"
            >
              {isConsiliumRunning ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--ink)]" />
                  <span>Совещание идет...</span>
                </>
              ) : (
                <>
                  <Play className="w-3.5 h-3.5 text-[var(--ink)]" />
                  <span>{attorneyNotes.trim() ? "Пересмотреть с правками" : "Собрать консилиум"}</span>
                </>
              )}
            </button>
          </div>

          {/* ВОПРОС БЕЗ ДЕЛА (медленная полоса).
              Отдельная кнопка, а не режим той же: разбор вопроса и разбор дела
              по документам — разные вещи, и путать их нельзя. */}
          <div className="glass-card p-3.5 rounded-2xl border border-[var(--line)] bg-[var(--surface-soft)]">
            <div className="flex items-center gap-2 mb-2">
              <MessagesSquare className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
              <h4 className="font-serif-legal text-xs font-bold text-[var(--gold-ink)]">
                Вопрос без дела
              </h4>
            </div>
            <p className="text-[10px] text-[var(--ink-muted-ink)] mb-2">
              Документов нет — Елена разберёт вопрос на проверяемые факты и назовёт, чего в нём
              не хватает. Идёт МИНУТАМИ: быстрый ответ спрашивайте у Катифи в виджете.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Например: клиенту отказали по I-485 из-за просрочки — что делать?"
                className="flex-1 bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)] placeholder:text-[var(--ink-muted-ink)] focus:outline-none focus:border-[var(--gold)]"
              />
              <button
                onClick={runQuestionConsilium}
                disabled={isConsiliumRunning || !questionText.trim()}
                className="gold-button px-3 py-2 rounded-xl flex items-center gap-2 text-xs font-bold whitespace-nowrap disabled:opacity-50"
              >
                {isConsiliumRunning ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--ink)]" />
                ) : (
                  <Play className="w-3.5 h-3.5 text-[var(--ink)]" />
                )}
                <span>Консилиум по вопросу</span>
              </button>
            </div>
          </div>

          {/* INTERACTIVE ATTORNEY DIRECTIVES CARD (EKATERINA FITENKO) */}
          <div className="glass-card p-3.5 rounded-2xl border border-[var(--line)] bg-[var(--surface-soft)] shadow-md">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-[var(--gold)] text-[var(--ink)] font-bold text-[10px] flex items-center justify-center shadow ring-1 ring-[var(--gold)]">
                  ЕФ
                </div>
                <div>
                  <h4 className="font-serif-legal text-xs font-bold text-[var(--gold-ink)]">
                    Указания и правовая позиция адвоката (Екатерина Фитенко, Esq.)
                  </h4>
                  <p className="text-[10px] text-[var(--ink-muted-ink)]">
                    Напишите комментарии — консилиум пересмотрит дело на базе ваших правок
                  </p>
                </div>
              </div>
              {attorneyNotes && (
                <button 
                  onClick={() => setAttorneyNotes('')}
                  className="text-[10px] text-[var(--ink-muted-ink)] hover:text-[var(--red-accent-ink)] transition"
                >
                  Очистить
                </button>
              )}
            </div>

            <textarea
              value={attorneyNotes}
              onChange={(e) => setAttorneyNotes(e.target.value)}
              placeholder="Например: Проверить доказательства совместного проживания (lease agreement), запросить налоговые формы W-2 за 2024 год и подготовить стратегию к интервью..."
              rows={2}
              className="w-full bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl p-2.5 text-xs text-[var(--ink)] placeholder-[var(--ink-muted)] focus:outline-none focus:border-[var(--gold)] resize-none"
            />

            {/* Quick chips */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[10px] text-[var(--ink-muted-ink)] self-center mr-0.5">Быстрые указания:</span>
              {[
                "⚠️ Проверить bona fide брак",
                "📄 Запросить W-2 и I-864",
                "⚡ Оценить риски интервью и I-94",
                "🔍 Проверить статус въезда"
              ].map((chip, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setAttorneyNotes(prev => prev ? `${prev}\n• ${chip}` : chip)}
                  className="text-[9px] px-2 py-0.5 rounded-md bg-[var(--surface-soft)] text-[var(--ink)] hover:text-[var(--gold-ink)] border border-[var(--line)] hover:border-[var(--gold)] transition"
                >
                  {chip}
                </button>
              ))}
            </div>

            {/* Re-run button */}
            <div className="mt-2.5 pt-2 border-t border-[var(--line)] flex items-center justify-between">
              <span className="text-[10px] text-[var(--ink-muted-ink)]">
                {attorneyNotes.trim() ? "Указания адвоката готовы к применению" : "Агенты примут указания как наивысший приоритет"}
              </span>
              <button
                type="button"
                onClick={() => runConsilium(attorneyNotes)}
                disabled={isConsiliumRunning}
                className="gold-button-outline px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Запустить повторный консилиум</span>
              </button>
            </div>
          </div>

          {/* Results Panel with Tabs */}
          <div className="glass-card p-4 rounded-2xl flex-1 flex flex-col">
            <div className="flex items-center justify-between border-b border-[var(--line)] pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setActiveTab('transcript')}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg flex items-center gap-1.5 transition ${
                    activeTab === 'transcript' ? 'bg-[var(--gold-soft)] text-[var(--gold-ink)] border border-[var(--line)]' : 'text-[var(--ink-muted-ink)] hover:text-[var(--ink)]'
                  }`}
                >
                  <MessagesSquare className="w-3 h-3" /> Совещание
                </button>
                <button
                  onClick={() => setActiveTab('memo')}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg flex items-center gap-1.5 transition ${
                    activeTab === 'memo' ? 'bg-[var(--gold-soft)] text-[var(--gold-ink)] border border-[var(--line)]' : 'text-[var(--ink-muted-ink)] hover:text-[var(--ink)]'
                  }`}
                >
                  <FileText className="w-3 h-3" /> Заключение (Memo)
                </button>
                <button
                  onClick={() => setActiveTab('gap')}
                  className={`text-xs font-semibold px-3 py-1 rounded-lg flex items-center gap-1.5 transition ${
                    activeTab === 'gap' ? 'bg-[var(--gold-soft)] text-[var(--gold-ink)] border border-[var(--line)]' : 'text-[var(--ink-muted-ink)] hover:text-[var(--ink)]'
                  }`}
                >
                  <CheckCircle className="w-3 h-3" /> Gap Analysis
                </button>
              </div>

              {consiliumResults && (
                <div className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[var(--surface-soft)] border border-[var(--line)] text-[var(--ink)]">
                  <span className="text-[var(--ink-muted-ink)]">Risk:</span>
                  <span className="text-[var(--gold-ink)]">
                    {consiliumResults.multi_agent_consilium?.structured_memo?.overall_risk_score || 32} / {consiliumResults.multi_agent_consilium?.structured_memo?.risk_level || 'Low'}
                  </span>
                </div>
              )}
            </div>

            {/* TAB 1: TRANSCRIPT */}
            {activeTab === 'transcript' && (
              <div className="space-y-3 overflow-y-auto max-h-[520px] pr-1 flex-1">
                {!consiliumResults ? (
                  <div className="text-center py-16 text-[var(--ink-muted-ink)]">
                    <Scale className="w-10 h-10 mx-auto mb-2 opacity-40 text-[var(--gold-ink)]" />
                    <Sostoyanie tekst="Консилиум ожидает материалы дела" />
                    <p className="text-[11px] text-[var(--ink-muted-ink)] mt-1">Выберите дело из списка слева и нажмите «Собрать консилиум»</p>
                  </div>
                ) : (
                  <>
                  {/* ─── РУССКИЙ ИТОГ. Стоит ВЫШЕ стенограммы намеренно: до
                      07.09.2026 адвокат получала четыре английских голоса и
                      искала ответ глазами, а строки «вот ответ» не было вовсе. */}
                  {(() => {
                    const ru = consiliumResults.multi_agent_consilium?.ru_summary;
                    if (!ru || ru.status === 'not_requested') {
                      return (
                        <div className="mb-3 p-3 rounded-xl border border-[var(--line)] bg-[var(--surface-soft)] text-[11px] text-[var(--ink-muted-ink)]">
                          Русская сводка в этом прогоне не запрашивалась. Это не отказ
                          и не пустой ответ — её просто не просили.
                        </div>
                      );
                    }
                    return (
                      <div className={`mb-3 p-3 rounded-xl border ${ru.status === 'ok'
                        ? 'border-[var(--line)] bg-[var(--surface-soft)]'
                        : 'border-red-500/50 bg-[var(--surface-soft)]'}`}>
                        {ru.status === 'ok' ? (
                          <>
                            <p className="font-serif-legal font-bold text-sm text-[var(--gold-ink)] mb-1.5">
                              Итог и расхождения
                            </p>
                            <p className="text-[12px] text-[var(--ink)] leading-relaxed whitespace-pre-wrap">
                              {ru.text}
                            </p>
                          </>
                        ) : (
                          /* 🔴 Провал сводки НАЗЫВАЕТСЯ провалом. Подстановка
                             английского синтеза на её место читалась бы как
                             «сводки не бывает» — ровно та болезнь, что чинилась. */
                          <p className="text-[12px] text-[var(--red-accent-ink)] leading-relaxed">
                            🔴 Русская сводка НЕ получена: {ru.error || 'причина не названа'}.
                            Ниже — разбор по-английски. Это отказ прибора, а не «сводки не бывает».
                          </p>
                        )}
                        {ru.dissent_line && (
                          <p className="mt-2 pt-2 border-t border-[var(--line)] text-[11px] text-[var(--ink-muted-ink)] whitespace-pre-wrap">
                            {ru.dissent_line}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* ─── ДОСТАВКА СПРАВКИ. Три исхода — три РАЗНЫХ сообщения. */}
                  {(() => {
                    const brief = consiliumResults.brief
                      || consiliumResults.multi_agent_consilium?.brief;
                    if (!brief) return null;
                    if (brief.pointer_error) {
                      return (
                        <div className="mb-3 p-3 rounded-xl border border-red-500/50 bg-[var(--surface-soft)] text-[11px] text-[var(--red-accent-ink)]">
                          Справка записана, но по идентификатору НЕДОСТИЖИМА: {brief.pointer_error}
                        </div>
                      );
                    }
                    if (brief.saved === false) {
                      return (
                        <div className="mb-3 p-3 rounded-xl border border-red-500/50 bg-[var(--surface-soft)] text-[11px] text-[var(--red-accent-ink)]">
                          Справка в свод НЕ записана: {brief.error || 'причина не названа'}
                        </div>
                      );
                    }
                    if (!lastJobId) return null;
                    return (
                      <div className="mb-3 p-3 rounded-xl border border-[var(--line)] bg-[var(--surface-soft)]">
                        <p className="text-[11px] text-[var(--ink-muted-ink)] mb-2 break-all">
                          Справка: <span className="text-[var(--gold-ink)]">{brief.rel_path}</span>
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={openBrief}
                            disabled={isBriefLoading}
                            className="gold-button-outline px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-50"
                          >
                            Открыть справку
                          </button>
                          <button
                            type="button"
                            onClick={downloadBriefDocx}
                            disabled={isBriefLoading}
                            className="gold-button-outline px-3 py-1 rounded-lg text-xs font-bold disabled:opacity-50"
                          >
                            Скачать DOCX
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {consiliumResults.multi_agent_consilium?.status === "DEGRADED" && (
                    <div className="mb-3 p-3 rounded-xl border border-red-500/50 bg-[var(--surface-soft)]">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-[var(--red-accent-ink)] shrink-0 mt-0.5" />
                        <div className="text-[11px] text-[var(--red-accent-ink)] leading-relaxed">
                          <p className="font-bold text-[var(--red-accent-ink)] mb-1">Консилиум неполный (DEGRADED)</p>
                          {(consiliumResults.multi_agent_consilium?.degraded_agents || []).length > 0 && (
                            <p>
                              <strong>Не отработали:</strong>{' '}
                              {consiliumResults.multi_agent_consilium.degraded_agents.join(", ")}
                            </p>
                          )}
                          {(consiliumResults.multi_agent_consilium?.substituted_agents || []).length > 0 && (
                            <p>
                              <strong>Отвечал другой провайдер (подмена):</strong>{' '}
                              {consiliumResults.multi_agent_consilium.substituted_agents.join(", ")}
                            </p>
                          )}
                          <p className="mt-1 text-[var(--red-accent-ink)]">
                            Заключение неполное. Пустой раздел означает отсутствие анализа,
                            а не отсутствие рисков.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {consiliumResults.multi_agent_consilium?.transcript?.map((turn, i) => {
                    let cleanAgentName = (turn.agent || "").replace(/\s*\([^)]*\)/g, "").trim();
                    let avatarInitials = "АВ";
                    let avatarBg = "from-[var(--gold)] to-[var(--gold)] text-[var(--ink)]";
                    let cardBorder = "border-[var(--line)] bg-[var(--surface)]";
                    let badgeColor = "bg-[var(--gold-soft)] text-[var(--gold-ink)]";

                    const isAttorney = turn.is_attorney || cleanAgentName.includes("Екатерина") || cleanAgentName.includes("Ekaterina");
                    const isKatifi = turn.is_katifi || cleanAgentName.includes("Катифи") || cleanAgentName.includes("Katifi");

                    if (isAttorney) {
                      cleanAgentName = "Екатерина Фитенко";
                      avatarInitials = "ЕФ";
                      avatarBg = "from-[var(--gold)] via-[var(--gold)] to-[var(--gold)] text-[var(--ink)] ring-2 ring-[var(--gold)]";
                      cardBorder = "border-[var(--gold)] bg-[var(--surface-soft)] shadow-lg ";
                      badgeColor = "bg-[var(--gold-soft)] text-[var(--gold-ink)] font-bold";
                    } else if (isKatifi) {
                      cleanAgentName = "Катифи";
                      avatarInitials = "КФ";
                      avatarBg = "from-emerald-600 to-teal-800 text-white ring-1 ring-emerald-400/40";
                      cardBorder = "border-emerald-500/40 bg-[var(--surface-soft)]";
                      badgeColor = "bg-[var(--surface-soft)] text-[var(--green-accent-ink)] font-mono";
                    } else if (cleanAgentName.includes("Елена") || cleanAgentName.includes("Elena")) {
                      cleanAgentName = "Елена";
                      avatarInitials = "ЕР";
                      avatarBg = "from-amber-700 to-yellow-600 text-white";
                      cardBorder = "border-amber-500/25 bg-[var(--surface-soft)]";
                      badgeColor = "bg-[var(--gold-soft)] text-[var(--amber-accent-ink)]";
                    } else if (cleanAgentName.includes("Маркус") || cleanAgentName.includes("Marcus")) {
                      cleanAgentName = "Маркус";
                      avatarInitials = "МС";
                      avatarBg = "from-emerald-800 to-teal-700 text-white";
                      cardBorder = "border-emerald-500/25 bg-[var(--surface-soft)]";
                      badgeColor = "bg-[var(--surface-soft)] text-[var(--green-accent-ink)]";
                    } else if (cleanAgentName.includes("Александр") || cleanAgentName.includes("Alexander")) {
                      cleanAgentName = "Александр";
                      avatarInitials = "АВ";
                      avatarBg = "from-[var(--gold)] to-[var(--gold)] text-[var(--ink)]";
                      cardBorder = "border-[var(--line)] bg-[var(--surface)]";
                      badgeColor = "bg-[var(--gold-soft)] text-[var(--gold-ink)]";
                    }

                    // Пустой раздел не должен читаться как «рисков нет»: помечаем провал.
                    if (turn.failed) {
                      cardBorder = "border-red-500/60 bg-[var(--surface-soft)]";
                    }

                    return (
                      <div key={i} className={`p-3.5 rounded-xl border ${cardBorder}`}>
                        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-[var(--line)]">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${avatarBg} font-bold text-[10px] flex items-center justify-center shadow shrink-0`}>
                              {avatarInitials}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-xs text-[var(--ink)]">{cleanAgentName}</span>
                                
                                {isAttorney ? (
                                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-[var(--gold-soft)] text-[var(--gold-ink)] font-bold border border-[var(--line)] flex items-center gap-1">
                                    <Scale className="w-2.5 h-2.5 text-[var(--gold-ink)]" />
                                    Ведущий адвокат (Partner)
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-[var(--gold-ink)] bg-[var(--gold-soft)] border border-[var(--line)] px-2 py-0.5 rounded-md font-medium">
                                    <User className="w-2.5 h-2.5 text-[var(--gold-ink)]" />
                                    Дело: <strong className="text-[var(--ink)] font-semibold">{activeCase.title || '—'}</strong>
                                  </span>
                                )}

                                <span className={`text-[8px] px-1.5 py-0.5 rounded font-mono ${badgeColor}`}>
                                  {turn.model || turn.ai_engine}
                                </span>
                                {turn.ai_engine && turn.model && (
                                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-[var(--gold-soft)] text-[var(--ink-muted-ink)]">
                                    {turn.ai_engine}
                                  </span>
                                )}
                                {turn.substituted && (
                                  <span className="text-[8px] px-1.5 py-0.5 rounded font-mono bg-[var(--gold-soft)] text-[var(--amber-accent-ink)] border border-amber-500/40">
                                    подменён
                                  </span>
                                )}
                                {turn.failed && (
                                  <span className="text-[8px] px-1.5 py-0.5 rounded font-mono bg-[var(--surface-soft)] text-[var(--red-accent-ink)] border border-red-500/40">
                                    не отработал
                                  </span>
                                )}
                              </div>
                              <p className="text-[9px] text-[var(--ink-muted-ink)] mt-0.5">{turn.title || turn.role}</p>
                            </div>
                          </div>
                          <span className="text-[9px] text-[var(--ink-muted-ink)] font-mono shrink-0">
                            {turn.timestamp ? turn.timestamp.split('T')[1].slice(0,5) : ''}
                          </span>
                        </div>

                        {isAttorney ? (
                          <div className="text-[11px] text-[var(--ink)] whitespace-pre-line leading-relaxed pl-9 font-medium bg-[var(--surface-soft)] p-2.5 rounded-lg border-l-2 border-[var(--gold)]">
                            {turn.message}
                          </div>
                        ) : (
                          <div className="text-[11px] text-[var(--ink)] whitespace-pre-line leading-relaxed pl-9">
                            {turn.message}
                          </div>
                        )}

                        {isKatifi && turn.obsidian_sync && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--green-accent-ink)] flex items-center gap-1.5 bg-[var(--surface-soft)] px-2.5 py-1 rounded-md border border-emerald-500/20">
                            <Sparkles className="w-3 h-3 text-[var(--green-accent-ink)] shrink-0" />
                            <span className="truncate">Obsidian Vault: {turn.obsidian_sync}</span>
                          </div>
                        )}

                        {isKatifi && !turn.obsidian_sync && turn.obsidian_error && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--red-accent-ink)] flex items-center gap-1.5 bg-[var(--surface-soft)] px-2.5 py-1 rounded-md border border-red-500/30">
                            <AlertTriangle className="w-3 h-3 text-[var(--red-accent-ink)] shrink-0" />
                            <span className="truncate">Obsidian Vault: НЕ сохранено — {turn.obsidian_error}</span>
                          </div>
                        )}

                        {/* Состояние свода. Пустая выдача и отказ прибора — разные новости,
                            и различие обязано доживать до экрана, а не гаснуть в бэкенде. */}
                        {isKatifi && turn.precedent_status === 'unavailable' && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--amber-accent-ink)] flex items-start gap-1.5 bg-[var(--surface-soft)] px-2.5 py-1 rounded-md border border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 text-[var(--amber-accent-ink)] shrink-0 mt-0.5" />
                            <span>Свод недоступен — сверка с прецедентами НЕ проводилась{turn.precedent_error ? `: ${turn.precedent_error}` : ''}. Это отказ прибора, а не проверенное отсутствие прецедентов.</span>
                          </div>
                        )}

                        {isKatifi && turn.precedent_status === 'empty' && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--ink-muted-ink)]">
                            Свод ответил: прецедентов по этой практике и фактам дела не найдено.
                          </div>
                        )}

                        {/* Расхождение Катифи — тремя видами. 🔴 null не сворачивается
                            в «расхождений нет»: молчание прибора не есть его показание. */}
                        {isKatifi && turn.dissent && turn.dissent.has_dissent === true && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--red-accent-ink)] bg-[var(--surface-soft)] px-2.5 py-1.5 rounded-md border border-red-500/40">
                            <div className="flex items-center gap-1.5 font-semibold text-[var(--red-accent-ink)]">
                              <AlertTriangle className="w-3 h-3 text-[var(--red-accent-ink)] shrink-0" />
                              <span>Заявлено расхождение с: {turn.dissent.with_whom || 'участник не назван'}</span>
                            </div>
                            <div className="mt-1">В чём: {turn.dissent.reason || 'причина не названа'}</div>
                            <div>Что меняется: {turn.dissent.changes || 'последствия не названы'}</div>
                          </div>
                        )}

                        {isKatifi && turn.dissent && turn.dissent.has_dissent === false && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--ink-muted-ink)]">
                            Расхождений с выводами консилиума не заявлено.
                          </div>
                        )}

                        {isKatifi && turn.dissent && turn.dissent.has_dissent == null && (
                          <div className="mt-2.5 ml-9 text-[10px] text-[var(--amber-accent-ink)] flex items-start gap-1.5 bg-[var(--surface-soft)] px-2.5 py-1 rounded-md border border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 text-[var(--amber-accent-ink)] shrink-0 mt-0.5" />
                            <span>Позиция Катифи не разобрана — считать неизвестным, а не согласием.</span>
                          </div>
                        )}

                      </div>
                    );
                  })}
                  </>
                )}
              </div>
            )}

            {/* TAB 2: LEGAL MEMO */}
            {activeTab === 'memo' && (
              <div className="space-y-3 overflow-y-auto max-h-[520px] pr-1 flex-1 text-xs text-[var(--ink)]">
                {consiliumResults?.multi_agent_consilium?.structured_memo ? (
                  <>
                    <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                      <h4 className="font-serif-legal text-sm font-bold text-[var(--gold-ink)] mb-1 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                        Executive Summary
                      </h4>
                      <p className="leading-relaxed text-[var(--ink)] text-[11px]">
                        {consiliumResults.multi_agent_consilium.structured_memo.executive_summary}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                        <h5 className="font-semibold text-[var(--gold-ink)] mb-1.5 text-[11px] flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-[var(--green-accent-ink)]" />
                          Ключевые факты
                        </h5>
                        <ul className="list-disc list-inside space-y-1 text-[var(--ink-muted-ink)] text-[10px]">
                          {consiliumResults.multi_agent_consilium.structured_memo.key_findings?.map((k, idx) => (
                            <li key={idx}>{k}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="p-3 rounded-xl bg-[var(--surface)] border border-rose-500/25">
                        <h5 className="font-semibold text-[var(--red-accent-ink)] mb-1.5 text-[11px] flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-[var(--red-accent-ink)]" />
                          Критические риски
                        </h5>
                        <ul className="list-disc list-inside space-y-1 text-[var(--ink-muted-ink)] text-[10px]">
                          {consiliumResults.multi_agent_consilium.structured_memo.critical_red_flags?.map((r, idx) => (
                            <li key={idx} className="text-[var(--red-accent-ink)]">{r}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                      <h5 className="font-semibold text-[var(--gold-ink)] mb-2 text-[11px] flex items-center gap-1.5">
                        <ListOrdered className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                        Процессуальные действия адвоката
                      </h5>
                      <ul className="space-y-1 text-[11px]">
                        {consiliumResults.multi_agent_consilium.structured_memo.attorney_next_actions?.map((act, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-[var(--ink)]">
                            <span className="w-3.5 h-3.5 rounded bg-[var(--gold-soft)] text-[var(--gold-ink)] text-[9px] font-mono flex items-center justify-center border border-[var(--line)]">
                              {idx + 1}
                            </span>
                            <span>{act}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Маркус заполнял это поле на КАЖДОМ прогоне, и оно никуда не
                        выводилось. Для иммиграционного дела «что ещё нужно от клиента»
                        — не второстепенная справка, а основной результат разбора. */}
                    {(consiliumResults.multi_agent_consilium.structured_memo.client_missing_requirements?.length > 0) && (
                      <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                        <h4 className="font-serif-legal text-sm font-bold text-[var(--gold-ink)] mb-2 flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                          Ещё нужно получить от клиента
                        </h4>
                        <ul className="space-y-1.5 text-[11px]">
                          {consiliumResults.multi_agent_consilium.structured_memo.client_missing_requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-[var(--ink)]">
                              <span className="w-1 h-1 rounded-full bg-[var(--gold)] shrink-0 mt-1.5" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Стороны и роли — тоже заполнялись и не показывались. */}
                    {(consiliumResults.multi_agent_consilium.structured_memo.parties_and_roles?.length > 0) && (
                      <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                        <h4 className="font-serif-legal text-sm font-bold text-[var(--gold-ink)] mb-2">Стороны и роли</h4>
                        <div className="space-y-1.5 text-[11px]">
                          {consiliumResults.multi_agent_consilium.structured_memo.parties_and_roles.map((p, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="font-semibold text-[var(--ink)] shrink-0">{p.name}</span>
                              <span className="text-[var(--gold-ink)] shrink-0">—</span>
                              <span className="text-[var(--ink-muted-ink)]">{p.role}{p.notes ? `. ${p.notes}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 text-[var(--ink-muted-ink)]">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-40 text-[var(--gold-ink)]" />
                    <Sostoyanie tekst="Заключение пока не составлено" />
                    <p className="text-[11px] text-[var(--ink-muted-ink)] mt-1">Запустите консилиум, чтобы сформировать Legal Memo</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: GAP ANALYSIS */}
            {activeTab === 'gap' && (
              <div className="space-y-3 overflow-y-auto max-h-[520px] pr-1 flex-1 text-xs">
                {consiliumResults?.document_gap_analysis ? (
                  <>
                    {/* Два РАЗНЫХ числа. Раньше было одно, и приложенное считалось проверенным. */}
                    <div className="p-3.5 rounded-xl bg-[var(--surface)] border border-[var(--line)] flex items-center justify-between gap-3">
                      <div>
                        <h4 className="font-serif-legal text-sm font-bold text-[var(--gold-ink)]">Индекс укомплектованности пакета</h4>
                        <p className="text-[10px] text-[var(--ink-muted-ink)] mt-0.5">Соответствие требованиям формы: {practiceLabel(selectedPractice)}</p>
                      </div>
                      <div className="flex items-center gap-4 text-right shrink-0">
                        <div>
                          <span className="block text-xl font-bold font-mono text-[var(--gold-ink)]">
                            {pctLabel(consiliumResults.document_gap_analysis.completion_percentage)}
                          </span>
                          <span className="block text-[9px] text-[var(--ink-muted-ink)]">подтверждено по содержимому</span>
                        </div>
                        <div>
                          <span className="block text-xl font-bold font-mono text-[var(--ink-muted-ink)]">
                            {pctLabel(consiliumResults.document_gap_analysis.attached_percentage)}
                          </span>
                          <span className="block text-[9px] text-[var(--ink-muted-ink)]">приложено (включая непроверенное)</span>
                        </div>
                      </div>
                    </div>

                    {/* Вердикт готовности. Бэкенд считал его с самого начала, но НИ ОДНО
                        место разметки его не читало — флаг, который дважды чинили
                        (загорался на пустых PDF, потом стал недостижим), адвокат не видел
                        вообще. Показываем ровно то, что означает поле, без смягчений. */}
                    <div className={`p-3 rounded-xl border flex items-start gap-2.5 ${
                      consiliumResults.document_gap_analysis.is_ready_for_filing
                        ? 'bg-[var(--surface-soft)] border-emerald-500/35'
                        : 'bg-[var(--surface-soft)] border-[var(--line)]'
                    }`}>
                      {consiliumResults.document_gap_analysis.is_ready_for_filing
                        ? <CheckCircle className="w-4 h-4 text-[var(--green-accent-ink)] shrink-0 mt-0.5" />
                        : <AlertTriangle className="w-4 h-4 text-[var(--gold-ink)] shrink-0 mt-0.5" />}
                      <div>
                        <p className={`font-semibold text-[12px] ${
                          consiliumResults.document_gap_analysis.is_ready_for_filing
                            ? 'text-[var(--green-accent-ink)]' : 'text-[var(--gold-ink)]'}`}>
                          {consiliumResults.document_gap_analysis.is_ready_for_filing
                            ? 'Все обязательные документы подтверждены по содержимому'
                            : 'К подаче не готово'}
                        </p>
                        <p className="text-[10px] text-[var(--ink-muted-ink)] mt-0.5">
                          {consiliumResults.document_gap_analysis.is_ready_for_filing
                            ? 'Это проверка комплектности, а не правовая оценка дела.'
                            : 'Подтверждение считается только по содержимому документа; приложенный файл с подходящим именем этого не заменяет.'}
                        </p>
                      </div>
                    </div>

                    {/* Что запросить у клиента. Бэкенд отдавал этот план на каждом прогоне
                        и он никуда не выводился — а это ровно то, ради чего адвокат
                        открывает Gap Analysis. */}
                    {(consiliumResults.document_gap_analysis.client_action_plan?.length > 0) && (
                      <div className="space-y-2">
                        <h5 className="font-semibold text-[var(--gold-ink)] text-[11px] flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                          Запросить у клиента ({consiliumResults.document_gap_analysis.client_action_plan.length})
                        </h5>
                        <div className="space-y-1.5">
                          {consiliumResults.document_gap_analysis.client_action_plan.map((a, idx) => (
                            <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                              <p className="font-semibold text-[var(--ink)] text-[11px]">{a.action_ru || a.action}</p>
                              {a.why && <p className="text-[10px] text-[var(--ink-muted-ink)] mt-1"><strong>Зачем:</strong> {a.why}</p>}
                              {a.how_to_get && <p className="text-[10px] text-[var(--ink-muted-ink)]"><strong>Где взять:</strong> {a.how_to_get}</p>}
                              {a.validity_note && <p className="text-[9px] text-[var(--gold-ink)] mt-1"><strong>Требование:</strong> {a.validity_note}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Файлы, которые бэкенд не принял. Молчаливой потери файла быть не должно. */}
                    {(consiliumResults.skipped_files?.length > 0) && (
                      <div className="space-y-2">
                        <h5 className="font-semibold text-[var(--red-accent-ink)] text-[11px] flex items-center gap-1.5">
                          <X className="w-3.5 h-3.5 text-[var(--red-accent-ink)]" />
                          Файлы не приняты ({consiliumResults.skipped_files.length})
                        </h5>
                        <div className="space-y-1.5">
                          {consiliumResults.skipped_files.map((s, idx) => (
                            <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface-soft)] border border-rose-500/25">
                              <p className="font-semibold text-[var(--red-accent-ink)] text-[11px] break-all">{s.filename}</p>
                              <p className="text-[10px] text-[var(--red-accent-ink)]">{s.reason}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Источник текста по каждому принятому документу */}
                    {(consiliumResults.processed_documents?.length > 0) && (
                      <div className="space-y-2">
                        <h5 className="font-semibold text-[var(--gold-ink)] text-[11px] flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                          Принятые документы ({consiliumResults.processed_documents.length})
                        </h5>
                        <div className="space-y-1.5">
                          {consiliumResults.processed_documents.map((doc, idx) => {
                            const src = documentSourceLabel(doc);
                            return (
                              <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface)] border border-[var(--line)]">
                                <p className="font-semibold text-[var(--ink)] text-[11px] break-all">{doc.filename}</p>
                                <p className="text-[10px] text-[var(--ink-muted-ink)]">
                                  Страниц: <span className="font-mono">{pagesLabel(doc)}</span>
                                  {" · "}символов: <span className="font-mono">{doc.extracted_chars ?? 0}</span>
                                  {" · "}<span className={src.tone}>{src.text}</span>
                                </p>
                                {doc.ocr_error && (
                                  <p className="text-[9px] text-[var(--red-accent-ink)] mt-1">Причина: {doc.ocr_error}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Нечитаемые — отдельным блоком, НЕ рядом с зелёными: они не подтверждены */}
                    {(consiliumResults.document_gap_analysis.unreadable_documents?.length > 0) && (
                      <div className="space-y-2">
                        <h5 className="font-semibold text-[var(--amber-accent-ink)] text-[11px] flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 text-[var(--amber-accent-ink)]" />
                          Приложено, но НЕ прочитано ({consiliumResults.document_gap_analysis.unreadable_documents.length})
                        </h5>
                        <div className="space-y-1.5">
                          {consiliumResults.document_gap_analysis.unreadable_documents.map((item, idx) => (
                            <div key={idx} className="p-2.5 rounded-xl bg-[var(--gold-soft)] border border-amber-500/30">
                              <p className="font-semibold text-[var(--amber-accent-ink)] text-[11px]">{item.name}</p>
                              <p className="text-[10px] text-[var(--amber-accent-ink)]">{item.name_ru}</p>
                              <p className="text-[9px] text-[var(--ink-muted-ink)] mt-1">
                                Файл: <span className="font-mono break-all">{item.matched_file}</span> — {item.match_note}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Приложено по имени, содержимое не подтверждает требование */}
                    {(consiliumResults.document_gap_analysis.attached_documents?.length > 0) && (
                      <div className="space-y-2">
                        <h5 className="font-semibold text-[var(--ink-muted-ink)] text-[11px] flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5 text-[var(--ink-muted-ink)]" />
                          Приложено без подтверждения содержимым ({consiliumResults.document_gap_analysis.attached_documents.length})
                        </h5>
                        <div className="space-y-1.5">
                          {consiliumResults.document_gap_analysis.attached_documents.map((item, idx) => (
                            <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface-soft)] border border-[var(--line)]">
                              <p className="font-semibold text-[var(--ink)] text-[11px]">{item.name}</p>
                              <p className="text-[10px] text-[var(--ink-muted-ink)]">{item.name_ru}</p>
                              <p className="text-[9px] text-[var(--ink-muted-ink)] mt-1">
                                Файл: <span className="font-mono break-all">{item.matched_file}</span> — {item.match_note}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 🔴 Раньше здесь читался ключ missing_mandatory_documents, которого бэкенд
                        НЕ возвращает: блок всегда показывал 0, а список был пуст всегда.
                        Реальный ключ — missing_documents, обязательные отбираются фильтром. */}
                    <div className="space-y-2">
                      <h5 className="font-semibold text-[var(--red-accent-ink)] text-[11px] flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-[var(--red-accent-ink)]" />
                        Недостающие обязательные документы ({(consiliumResults.document_gap_analysis.missing_documents || []).filter(i => i.is_mandatory).length})
                      </h5>
                      <div className="space-y-1.5">
                        {(consiliumResults.document_gap_analysis.missing_documents || []).filter(i => i.is_mandatory).map((item, idx) => (
                          <div key={idx} className="p-2.5 rounded-xl bg-[var(--surface-soft)] border border-rose-500/25 flex items-start gap-2">
                            <span className="w-3.5 h-3.5 rounded bg-[var(--surface-soft)] text-[var(--red-accent-ink)] text-[9px] font-mono flex items-center justify-center shrink-0 mt-0.5">!</span>
                            <div>
                              <p className="font-semibold text-[var(--red-accent-ink)] text-[11px]">{item.name}</p>
                              <p className="text-[10px] text-[var(--red-accent-ink)]">{item.name_ru}</p>
                              {item.weak_match && (
                                <p className="text-[9px] text-[var(--amber-accent-ink)] mt-1">{item.match_note}</p>
                              )}
                              <p className="text-[9px] text-[var(--ink-muted-ink)] mt-1"><strong>Требование:</strong> {item.validity_requirements}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-16 text-[var(--ink-muted-ink)]">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40 text-[var(--gold-ink)]" />
                    <p className="font-serif-legal text-base font-semibold text-[var(--ink-muted-ink)]">Gap Analysis еще не проводился</p>
                    <p className="text-[11px] text-[var(--ink-muted-ink)] mt-1">Нажмите «Собрать консилиум» для аудита документов</p>
                  </div>
                )}
              </div>
            )}

          </div>

        </div>

      </main>

      {/* KATIFI DAILY REPORT MODAL */}
      {isReportModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[var(--surface-soft)] border border-[var(--line)] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between bg-[var(--surface-soft)]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-800 text-white font-bold text-xs flex items-center justify-center shadow ring-1 ring-emerald-400">
                  КФ
                </div>
                <div>
                  <h3 className="font-serif-legal font-bold text-base text-[var(--gold-ink)]">
                    Ежедневный отчет Katifi по иммиграционному докету
                  </h3>
                  <p className="text-xs text-[var(--ink-muted-ink)]">
                    Хранилище Obsidian: <code className="text-[var(--green-accent-ink)] font-mono">_obsidian_kb/fitenko/katifi-writes/reports/</code>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsReportModalOpen(false)}
                className="text-[var(--ink-muted-ink)] hover:text-[var(--ink)] p-1.5 rounded-lg hover:bg-white/5 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto flex-1 space-y-4 text-xs">
              {isGeneratingReport ? (
                <div className="py-16 text-center text-[var(--ink-muted-ink)]">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[var(--gold-ink)]" />
                  <p className="font-semibold text-sm text-[var(--gold-ink)]">Katifi проводит аудит активного докета...</p>
                  <p className="text-[11px] text-[var(--ink-muted-ink)] mt-1">Сбор статистики, рисков, указаний адвоката и сохранение в Obsidian</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-[var(--line)]">
                    <span className="text-[11px] text-[var(--ink-muted-ink)]">
                      Путь к файлу: <strong className={obsidianPath ? "text-[var(--green-accent-ink)] font-mono" : "text-[var(--red-accent-ink)] font-mono"}>
                        {obsidianPath || 'НЕ сохранено'}
                      </strong>
                    </span>
                    {obsidianPath ? (
                      <span className="px-2 py-0.5 rounded-full bg-[var(--surface-soft)] text-[var(--green-accent-ink)] font-mono text-[10px] border border-emerald-500/30">
                        Obsidian Vault Synced
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-[var(--surface-soft)] text-[var(--red-accent-ink)] font-mono text-[10px] border border-red-500/30">
                        Obsidian Vault NOT synced
                      </span>
                    )}
                  </div>
                  <pre className="bg-[var(--surface-soft)] border border-[var(--line)] rounded-xl p-4 text-[11px] font-mono text-[var(--ink)] whitespace-pre-wrap leading-relaxed overflow-x-auto max-h-[480px]">
                    {katifiReportText}
                  </pre>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3.5 border-t border-[var(--line)] flex items-center justify-between bg-[var(--surface)]">
              <span className="text-[11px] text-[var(--ink-muted-ink)]">
                Ведущий адвокат: <strong className="text-[var(--gold-ink)]">Екатерина Фитенко, Esq.</strong>
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={generateKatifiReport}
                  disabled={isGeneratingReport}
                  className="gold-button px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[var(--ink)]" />
                  <span>{isGeneratingReport ? "Формирование..." : "Обновить и записать в Obsidian"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsReportModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--ink-muted-ink)] hover:text-[var(--ink)]"
                >
                  Закрыть
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* NEW CASE MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[var(--surface-soft)] border border-[var(--line)] rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between bg-[var(--surface-soft)]">
              <div className="flex items-center gap-2.5">
                <Briefcase className="w-5 h-5 text-[var(--gold-ink)]" />
                <div>
                  <h3 className="font-serif-legal font-bold text-base text-[var(--gold-ink)]">
                    Регистрация нового иммиграционного дела
                  </h3>
                  <p className="text-xs text-[var(--ink-muted-ink)]">Внесение клиента в докет и генерация чек-листа USCIS</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-[var(--ink-muted-ink)] hover:text-[var(--ink)] p-1.5 rounded-lg hover:bg-white/5 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Form Content */}
            <div className="p-5 overflow-y-auto flex-1 space-y-4 text-xs">
              
              {/* Block 1 */}
              <div className="bg-[var(--surface-soft)] border border-[var(--line)] p-3.5 rounded-xl space-y-3">
                <h4 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)] pb-1 border-b border-[var(--line)]">
                  1. Категория и основной заявитель (Beneficiary)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Категория петиции</label>
                    <select
                      value={modalCategory}
                      onChange={(e) => setModalCategory(e.target.value)}
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    >
                      <option value="">— выберите практику —</option>
                      {supportedPractices.map(key => (
                        <option key={key} value={key}>{PRACTICE_TITLES_RU[key] || key}</option>
                      ))}
                      {supportedPractices.length === 0 && (
                        <option value="" disabled>Список практик не загружен</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">ФИО заявителя (латиницей и кириллицей) *</label>
                    <input
                      type="text"
                      value={modalClientName}
                      onChange={(e) => setModalClientName(e.target.value)}
                      placeholder="Иванов, Иван / Ivanov, Ivan"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Email клиента</label>
                    <input
                      type="email"
                      value={modalClientEmail}
                      onChange={(e) => setModalClientEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                    <p className="text-[9px] text-[var(--ink-muted-ink)] mt-1">
                      Нужно указать почту ИЛИ телефон — по любому из них дело связывается
                      с клиентом. Если такого клиента в системе нет, он будет заведён;
                      письмо ему НЕ отправляется ни в каком случае.
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Телефон клиента</label>
                    <input
                      type="tel"
                      value={modalContact}
                      onChange={(e) => setModalContact(e.target.value)}
                      placeholder="+1 (000) 000-0000"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                    <p className="text-[9px] text-[var(--ink-muted-ink)] mt-1">
                      Достаточно вместо почты. Один и тот же номер, записанный по-разному,
                      находит ОДНОГО клиента. Без почты клиент заводится с техническим
                      адресом — писать на него нельзя.
                    </p>
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Имя клиента (First name) *</label>
                    <input
                      type="text"
                      value={modalClientFirstName}
                      onChange={(e) => setModalClientFirstName(e.target.value)}
                      placeholder="Имя как в паспорте"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Фамилия клиента (Last name)</label>
                    <input
                      type="text"
                      value={modalClientLastName}
                      onChange={(e) => setModalClientLastName(e.target.value)}
                      placeholder="Фамилия как в паспорте"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Гражданство / Подданство</label>
                    <input
                      type="text"
                      value={modalCitizenship}
                      onChange={(e) => setModalCitizenship(e.target.value)}
                      placeholder="Украина, Казахстан, Израиль..."
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Alien Registration Number (A-Number)</label>
                    <input
                      type="text"
                      value={modalANumber}
                      onChange={(e) => setModalANumber(e.target.value)}
                      placeholder="A# 000-000-000 (если есть)"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                </div>
              </div>

              {/* Block 2 */}
              <div className="bg-[var(--surface-soft)] border border-[var(--line)] p-3.5 rounded-xl space-y-3">
                <h4 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)] pb-1 border-b border-[var(--line)]">
                  2. Петиционер / Супруг(а) (US Citizen / LPR Sponsor)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">ФИО супруга / петиционера</label>
                    <input
                      type="text"
                      value={modalSpouseName}
                      onChange={(e) => setModalSpouseName(e.target.value)}
                      placeholder="Smith, Johnathan"
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Иммиграционный статус петиционера</label>
                    <select
                      value={modalSpouseStatus}
                      onChange={(e) => setModalSpouseStatus(e.target.value)}
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    >
                      <option value="Гражданин США (по рождению)">Гражданин США (по рождению)</option>
                      <option value="Гражданин США (натурализация)">Гражданин США (натурализация)</option>
                      <option value="Постоянный резидент (LPR / Green Card)">Постоянный резидент (LPR / Green Card)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Block 3 */}
              <div className="bg-[var(--surface-soft)] border border-[var(--line)] p-3.5 rounded-xl space-y-3">
                <h4 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)] pb-1 border-b border-[var(--line)]">
                  3. Параметры въезда в США (I-94 & Inspection)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Дата последнего въезда</label>
                    <input
                      type="date"
                      value={modalEntryDate}
                      onChange={(e) => setModalEntryDate(e.target.value)}
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Порт въезда</label>
                    <input
                      type="text"
                      value={modalPortOfEntry}
                      onChange={(e) => setModalPortOfEntry(e.target.value)}
                      placeholder="JFK, MIA, LAX..."
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-[var(--ink-muted-ink)] mb-1">Класс допуска</label>
                    <select
                      value={modalClassOfAdmission}
                      onChange={(e) => setModalClassOfAdmission(e.target.value)}
                      className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl px-3 py-2 text-xs text-[var(--ink)]"
                    >
                      <option value="B2 Visitor">B2 Visitor</option>
                      <option value="Humanitarian Parole">Humanitarian Parole (U4U/CBP)</option>
                      <option value="F-1 Student">F-1 Student</option>
                      <option value="H-1B Worker">H-1B Worker</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Block 4 */}
              <div className="bg-[var(--surface-soft)] border border-[var(--line)] p-3.5 rounded-xl space-y-3">
                <h4 className="font-serif-legal font-bold text-sm text-[var(--gold-ink)] pb-1 border-b border-[var(--line)]">
                  4. Фабула дела и Red Flags
                </h4>
                <textarea
                  rows={3}
                  value={modalCaseContext}
                  onChange={(e) => setModalCaseContext(e.target.value)}
                  placeholder="Опишите хронологию событий, основания петиции, место работы..."
                  className="w-full bg-[var(--surface)] border border-[var(--line)] rounded-xl p-3 text-xs text-[var(--ink)] placeholder-[var(--ink-muted)]"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-[var(--ink-muted-ink)]">
                  <label className="flex items-center gap-2 p-2 rounded-lg bg-[var(--surface)] border border-[var(--line)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={riskFlags.overstay}
                      onChange={(e) => setRiskFlags(r => ({ ...r, overstay: e.target.checked }))}
                    />
                    <span>Превышение срока пребывания в США (Overstay)</span>
                  </label>
                  <label className="flex items-center gap-2 p-2 rounded-lg bg-[var(--surface)] border border-[var(--line)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={riskFlags.priorDenials}
                      onChange={(e) => setRiskFlags(r => ({ ...r, priorDenials: e.target.checked }))}
                    />
                    <span>Предыдущие отказы в визах США или петициях</span>
                  </label>
                </div>
              </div>

            </div>

            <div className="px-5 py-3.5 border-t border-[var(--line)] flex items-center justify-between bg-[var(--surface)]">
              <span className="text-[11px] text-[var(--ink-muted-ink)] flex items-center gap-1">
                <Scale className="w-3.5 h-3.5 text-[var(--gold-ink)]" />
                Fitenko Law P.L.L.C. Security & Attorney-Client Privilege
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-[var(--ink-muted-ink)] hover:text-[var(--ink)]"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={handleCreateCase}
                  disabled={isCreatingCase}
                  className="gold-button px-5 py-2 rounded-xl text-xs flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 text-[var(--ink)]" />
                  <span>{isCreatingCase ? "Создаём дело..." : "Создать дело и открыть чек-лист"}</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AIParalegal;
